'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { createClient } from '@/lib/supabase/client';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import EstimateBuilder from '@/components/common/EstimateBuilder';
import type { EstimateSummaryTotals, EstimateLineItem } from '@/components/common/EstimateBuilder';
import PermitDetailModal from '@/app/permits/components/PermitDetailModal';
import type { Permit } from '@/app/permits/components/types';
import AddCustomerModal from '@/app/customer-dashboard/components/AddCustomerModal';
import InspectionBuilder from '@/app/customer-dashboard/components/InspectionBuilder';
import RoofMeasureTool from '@/app/customer-dashboard/components/RoofMeasureTool';

// ─── Types ────────────────────────────────────────────────────────────────────

export type StageKey =
  | 'new-lead' | 'appointment-scheduled' | 'working-lead' | 'unable-to-contact'
  | 'insurance-pending' | 'adjuster-meeting' | 'pending-ins-approval' | 'overturn'
  | 'pa' | 'insurance-approved' | 'estimating' | 'pending-review'
  | 'rts' | 'signed-contract' | 'contact-at-later-date';

export type PaymentSubStatus = 'financing' | 'escrow' | 'ach' | null;

export interface CustomerCard {
  id: string;
  jobId: string;
  customerFirstName: string;
  customerLastName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  totalSquares: number;
  instantEstimate: number;
  contractValue: number;
  material: string | null;
  shingleColor: string | null;
  costPerSquare: number | null;
  financingStatus: string | null;
  escrowStatus: string | null;
  paymentMethod: string | null;
  referralSource: string | null;
  notes: string | null;
  stage: StageKey;
  paymentSubStatus: PaymentSubStatus;
  columnEnteredAt: string;
  appointmentDate: string | null;
  appointmentWindow: string | null;
  appointmentType: string | null;
  vpmName: string;
  qcName: string;
  contactType: string | null;
  // Manual-add / JobNimbus fields
  displayName: string | null;
  phoneType: string | null;
  phoneExt: string | null;
  location: string | null;
  jobType: string | null;
  assignedTo: string | null;
  addressLine2: string | null;
  country: string | null;
  jobScheduledDate: string | null;
  jobTime: string | null;
  gatedCommunity: boolean | null;
  gateCode: string | null;
  dateOfLoss: string | null;
  insuranceCompany: string | null;
  claimNumber: string | null;
  policyNumber: string | null;
  adjusterName: string | null;
  adjusterPhone: string | null;
  adjusterEmail: string | null;
  outsideAdjuster: string | null;
  outsideAdjusterEmail: string | null;
  outsideAdjusterNumber: string | null;
  salesrabbitId: string | null;
  company: string | null;
  website: string | null;
}

type TabId = 'overview' | 'crew' | 'logistics' | 'photos' | 'inspection' | 'milestones' | 'notes' | 'documents' | 'measurements' | 'estimate' | 'permits';

// ─── Constants ────────────────────────────────────────────────────────────────

export const PIPELINE_COLUMNS: { id: StageKey; title: string; subtitle: string; color: string }[] = [
  { id: 'new-lead',              title: 'New Lead',              subtitle: 'New Lead, Lead',             color: '#3B82F6' },
  { id: 'appointment-scheduled', title: 'Appointment Scheduled', subtitle: 'Appointment Scheduled',      color: '#6366F1' },
  { id: 'working-lead',          title: 'Working Lead',          subtitle: 'Working Lead',               color: '#8B5CF6' },
  { id: 'unable-to-contact',     title: 'Unable to Contact',     subtitle: 'Unable to Contact',          color: '#64748B' },
  { id: 'insurance-pending',     title: 'Insurance Pending',     subtitle: 'Insurance Pending',          color: '#F59E0B' },
  { id: 'adjuster-meeting',      title: 'Adjuster Meeting',      subtitle: 'Adjuster Meeting Scheduled', color: '#EC4899' },
  { id: 'pending-ins-approval',  title: 'Pending Ins Approval',  subtitle: 'Pending Ins Approval',       color: '#A855F7' },
  { id: 'overturn',              title: 'Overturn',              subtitle: 'Overturn',                   color: '#EF4444' },
  { id: 'pa',                    title: 'PA',                    subtitle: 'PA',                         color: '#14B8A6' },
  { id: 'insurance-approved',    title: 'Insurance Approved',    subtitle: 'Insurance Approved',         color: '#10B981' },
  { id: 'estimating',            title: 'Estimating',            subtitle: 'Estimating',                 color: '#0EA5E9' },
  { id: 'pending-review',        title: 'Pending Review',        subtitle: 'Contract Review',            color: '#F97316' },
  { id: 'rts',                   title: 'RTS',                   subtitle: 'Pending Customer Signature', color: '#7C3AED' },
  { id: 'signed-contract',       title: 'Signed Contract',       subtitle: 'Signed Contract',            color: '#059669' },
  { id: 'contact-at-later-date', title: 'Contact at Later Date', subtitle: 'Contact at Later Date',      color: '#94A3B8' },
];

const STAGE_ORDER: StageKey[] = [
  'new-lead', 'appointment-scheduled', 'working-lead', 'unable-to-contact',
  'insurance-pending', 'adjuster-meeting', 'pending-ins-approval', 'overturn',
  'pa', 'insurance-approved', 'estimating', 'pending-review',
  'rts', 'signed-contract', 'contact-at-later-date',
];

export function isValidTransition(from: StageKey, to: StageKey): boolean {
  // All transitions are allowed — callers show soft warnings for backward moves
  return from !== to;
}

export function isBackwardTransition(from: StageKey, to: StageKey): boolean {
  return STAGE_ORDER.indexOf(to) < STAGE_ORDER.indexOf(from);
}

export function getValidNextStages(current: StageKey): StageKey[] {
  // Return all other stages (both forward and backward)
  return STAGE_ORDER.filter(s => s !== current);
}

const TABS: { id: TabId; label: string; icon: string }[] = [
  { id: 'overview',     label: 'Overview',     icon: 'HomeIcon' },
  { id: 'crew',         label: 'Crew',         icon: 'UsersIcon' },
  { id: 'logistics',    label: 'Logistics',    icon: 'TruckIcon' },
  { id: 'photos',       label: 'Photos',       icon: 'PhotoIcon' },
  { id: 'inspection',   label: 'Inspection',   icon: 'ClipboardDocumentCheckIcon' },
  { id: 'milestones',   label: 'Milestones',   icon: 'FlagIcon' },
  { id: 'notes',        label: 'Notes',        icon: 'PencilSquareIcon' },
  { id: 'documents',    label: 'Documents',    icon: 'DocumentTextIcon' },
  { id: 'measurements', label: 'Measurements', icon: 'CalculatorIcon' },
  { id: 'estimate',     label: 'Estimate',     icon: 'CurrencyDollarIcon' },
  { id: 'permits',      label: 'Permits',      icon: 'DocumentCheckIcon' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function mapPipelineRow(row: any): CustomerCard {
  const stage = row.stage as StageKey;
  const paymentSubStatus: PaymentSubStatus = null;

  return {
    id: row.id,
    jobId: row.job_id,
    customerFirstName: row.customer_first_name ?? '',
    customerLastName: row.customer_last_name ?? '',
    email: row.email ?? '',
    phone: row.phone ?? '',
    address: row.address ?? '',
    city: row.city ?? '',
    state: row.state ?? '',
    zip: row.zip ?? '',
    totalSquares: Number(row.total_squares ?? 0),
    instantEstimate: Number(row.instant_estimate ?? 0),
    contractValue: Number(row.contract_value ?? 0),
    material: row.material ?? null,
    shingleColor: row.shingle_color ?? null,
    costPerSquare: row.cost_per_square != null ? Number(row.cost_per_square) : null,
    financingStatus: row.financing_status ?? null,
    escrowStatus: row.escrow_status ?? null,
    paymentMethod: row.payment_method ?? null,
    referralSource: row.referral_source ?? null,
    notes: row.notes ?? null,
    stage,
    paymentSubStatus,
    columnEnteredAt: row.column_entered_at ?? row.created_at ?? new Date().toISOString(),
    appointmentDate: row.appointment_date ?? null,
    appointmentWindow: row.appointment_window ?? null,
    appointmentType: row.appointment_type ?? null,
    vpmName: row.vpm_name ?? '',
    qcName: row.qc_name ?? '',
    contactType: row.contact_type ?? null,
    displayName: row.display_name ?? null,
    phoneType: row.phone_type ?? null,
    phoneExt: row.phone_ext ?? null,
    location: row.location ?? null,
    jobType: row.job_type ?? null,
    assignedTo: row.assigned_to ?? null,
    addressLine2: row.address_line2 ?? null,
    country: row.country ?? null,
    jobScheduledDate: row.job_scheduled_date ?? null,
    jobTime: row.job_time ?? null,
    gatedCommunity: row.gated_community ?? null,
    gateCode: row.gate_code ?? null,
    dateOfLoss: row.date_of_loss ?? null,
    insuranceCompany: row.insurance_company ?? null,
    claimNumber: row.claim_number ?? null,
    policyNumber: row.policy_number ?? null,
    adjusterName: row.adjuster_name ?? null,
    adjusterPhone: row.adjuster_phone ?? null,
    adjusterEmail: row.adjuster_email ?? null,
    outsideAdjuster: row.outside_adjuster ?? null,
    outsideAdjusterEmail: row.outside_adjuster_email ?? null,
    outsideAdjusterNumber: row.outside_adjuster_number ?? null,
    salesrabbitId: row.salesrabbit_id ?? null,
    company: row.company ?? null,
    website: row.website ?? null,
  };
}

function formatCurrency(val: number): string {
  if (!val) return 'TBD';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val);
}

function getDaysInColumn(columnEnteredAt: string): number {
  const entered = new Date(columnEnteredAt).getTime();
  const diffMs = Date.now() - entered;
  return Math.max(1, Math.floor(diffMs / (1000 * 60 * 60 * 24)) + 1);
}

function getDaysColor(days: number): { bg: string; text: string } {
  if (days <= 3) return { bg: '#DCFCE7', text: '#15803D' };
  if (days <= 7) return { bg: '#FEF9C3', text: '#A16207' };
  return { bg: '#FEE2E2', text: '#B91C1C' };
}

// ─── Mini Availability Calendar ───────────────────────────────────────────────

interface MiniAvailabilityCalendarProps {
  userName: string;
  assignmentCount: number | null;
  accentColor: 'blue' | 'green';
  calendarMonth: Date;
}

function MiniAvailabilityCalendar({ userName, assignmentCount, accentColor, calendarMonth }: MiniAvailabilityCalendarProps) {
  const today = new Date();
  const year = calendarMonth.getFullYear();
  const month = calendarMonth.getMonth();
  const monthName = calendarMonth.toLocaleString('default', { month: 'long', year: 'numeric' });

  // Build calendar grid
  const firstDay = new Date(year, month, 1).getDay(); // 0=Sun
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells: (number | null)[] = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const isToday = (d: number) =>
    today.getFullYear() === year && today.getMonth() === month && today.getDate() === d;

  // Workload color
  const count = assignmentCount ?? 0;
  const workloadColor = count === 0 ? 'text-green-600 bg-green-50' : count <= 3 ? 'text-yellow-700 bg-yellow-50' : 'text-red-600 bg-red-50';
  const workloadLabel = count === 0 ? 'Available' : count <= 3 ? 'Moderate load' : 'High load';
  const workloadDot = count === 0 ? 'bg-green-500' : count <= 3 ? 'bg-yellow-500' : 'bg-red-500';

  const accentTodayBg = accentColor === 'blue' ? 'bg-blue-600 text-white' : 'bg-green-600 text-white';
  const dayLabels = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  return (
    <div className="mt-2 bg-white border border-gray-200 rounded-lg p-3 shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-gray-700">{monthName}</span>
        {assignmentCount !== null ? (
          <span className={`flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${workloadColor}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${workloadDot}`} />
            {workloadLabel} · {count} active
          </span>
        ) : (
          <span className="text-xs text-gray-400">Loading…</span>
        )}
      </div>

      {/* Day labels */}
      <div className="grid grid-cols-7 gap-0.5 mb-1">
        {dayLabels.map(d => (
          <div key={d} className="text-center text-gray-400 font-medium" style={{ fontSize: '9px' }}>{d}</div>
        ))}
      </div>

      {/* Calendar cells */}
      <div className="grid grid-cols-7 gap-0.5">
        {cells.map((d, i) => (
          <div
            key={i}
            className={`h-5 w-full flex items-center justify-center rounded text-gray-600 ${
              d === null ? '' : isToday(d) ? `${accentTodayBg} font-bold rounded-full` : 'hover:bg-gray-100'
            }`}
            style={{ fontSize: '10px' }}
          >
            {d ?? ''}
          </div>
        ))}
      </div>

      {/* Footer note */}
      <p className="mt-2 text-gray-400 leading-tight" style={{ fontSize: '9px' }}>
        Workload based on active pipeline assignments for {userName}
      </p>
    </div>
  );
}

// ─── Change Order Modal ───────────────────────────────────────────────────────

function ChangeOrderModal({ jobId, customerName, onClose }: { jobId: string; customerName: string; onClose: () => void }) {
  const [form, setForm] = useState({ description: '', reason: '', amount: '', priority: 'medium', notes: '' });
  const [submitted, setSubmitted] = useState(false);

  return (
    <div className="fixed inset-0 bg-black/50 z-[160] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="font-heading text-lg font-bold text-foreground">Create Change Order</h2>
            <p className="font-caption text-xs text-muted-foreground">Job #{jobId} · {customerName}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-gray-100 transition-colors" aria-label="Close modal">
            <Icon name="XMarkIcon" size={20} />
          </button>
        </div>
        {submitted ? (
          <div className="px-6 py-10 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <Icon name="CheckCircleIcon" size={32} variant="solid" className="text-green-600" />
            </div>
            <h3 className="font-heading text-lg font-semibold text-foreground mb-2">Change Order Submitted</h3>
            <p className="font-body text-sm text-muted-foreground mb-6">The change order has been sent for digital signature approval.</p>
            <button onClick={onClose} className="px-6 py-2.5 rounded-lg font-body text-sm font-medium text-white transition-opacity hover:opacity-90" style={{ backgroundColor: '#1C2E5E' }}>Close</button>
          </div>
        ) : (
          <form onSubmit={e => { e.preventDefault(); setSubmitted(true); }} className="px-6 py-5 space-y-4">
            <div>
              <label className="font-caption text-xs font-medium text-foreground block mb-1.5">Change Description <span className="text-red-500">*</span></label>
              <textarea required rows={3} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Describe the change order in detail..." className="w-full px-3 py-2 rounded-lg border border-gray-200 font-body text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 resize-none" />
            </div>
            <div>
              <label className="font-caption text-xs font-medium text-foreground block mb-1.5">Reason for Change <span className="text-red-500">*</span></label>
              <select required value={form.reason} onChange={e => setForm(p => ({ ...p, reason: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-gray-200 font-body text-sm focus:outline-none focus:ring-2 focus:ring-blue-300">
                <option value="">Select reason...</option>
                <option value="customer-request">Customer Request</option>
                <option value="material-upgrade">Material Upgrade</option>
                <option value="structural-issue">Structural Issue Found</option>
                <option value="weather-damage">Weather Damage</option>
                <option value="code-compliance">Code Compliance</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="font-caption text-xs font-medium text-foreground block mb-1.5">Additional Cost (USD)</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">$</span>
                  <input type="number" min="0" step="0.01" value={form.amount} onChange={e => setForm(p => ({ ...p, amount: e.target.value }))} placeholder="0.00" className="w-full pl-7 pr-3 py-2 rounded-lg border border-gray-200 font-body text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
                </div>
              </div>
              <div>
                <label className="font-caption text-xs font-medium text-foreground block mb-1.5">Priority</label>
                <select value={form.priority} onChange={e => setForm(p => ({ ...p, priority: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-gray-200 font-body text-sm focus:outline-none focus:ring-2 focus:ring-blue-300">
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
            </div>
            <div>
              <label className="font-caption text-xs font-medium text-foreground block mb-1.5">Additional Notes</label>
              <textarea rows={2} value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Any additional context..." className="w-full px-3 py-2 rounded-lg border border-gray-200 font-body text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 resize-none" />
            </div>
            <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2.5">
              <Icon name="InformationCircleIcon" size={16} variant="solid" className="text-blue-500 flex-shrink-0" />
              <p className="font-caption text-xs text-blue-700">Submitting will trigger a digital signature workflow for customer approval.</p>
            </div>
            <div className="flex gap-3 pt-1">
              <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-lg border border-gray-200 font-body text-sm font-medium text-foreground hover:bg-gray-50 transition-colors">Cancel</button>
              <button type="submit" className="flex-1 py-2.5 rounded-lg font-body text-sm font-medium text-white transition-colors" style={{ backgroundColor: '#1C2E5E' }}>Submit Change Order</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ─── Props ────────────────────────────────────────────────────────────────────

export interface CustomerCardPanelProps {
  card: CustomerCard;
  onClose: () => void;
  onStageChange?: (cardId: string, stage: StageKey) => void;
}

// ─── Main Panel ───────────────────────────────────────────────────────────────

export default function CustomerCardPanel({ card, onClose, onStageChange }: CustomerCardPanelProps) {
  const supabase = createClient();
  const router = useRouter();
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [showChangeOrder, setShowChangeOrder] = useState(false);
  const [showEditCustomer, setShowEditCustomer] = useState(false);
  const [dialNotification, setDialNotification] = useState<string | null>(null);

  // Permits (scoped to this customer's job) — lazily loaded when the tab opens
  const [permit, setPermit] = useState<Permit | null>(null);
  const [permitLoading, setPermitLoading] = useState(false);
  const [permitFetched, setPermitFetched] = useState(false);
  const [permitCreating, setPermitCreating] = useState(false);
  const [showPermitDetail, setShowPermitDetail] = useState(false);

  // Load this job's permit the first time the Permits tab is opened.
  useEffect(() => {
    if (activeTab !== 'permits' || permitFetched || !card.jobId) return;
    let cancelled = false;
    setPermitLoading(true);
    supabase
      .from('permits')
      .select('*')
      .eq('job_id', card.jobId)
      .limit(1)
      .then(({ data }: { data: any }) => {
        if (cancelled) return;
        setPermit((data && data[0]) || null);
        setPermitFetched(true);
        setPermitLoading(false);
      });
    return () => { cancelled = true; };
  }, [activeTab, permitFetched, card.jobId, supabase]);

  const handleCreatePermit = async () => {
    if (!card.jobId || permitCreating) return;
    setPermitCreating(true);
    try {
      // Array-form .select() after write (never .single() — see CLAUDE.md).
      const { data, error } = await supabase
        .from('permits')
        .insert({ job_id: card.jobId, status: 'needs_filing' })
        .select('*');
      if (error) throw error;
      const created = data?.[0] || null;
      setPermit(created);
      if (created) setShowPermitDetail(true);
    } catch (err) {
      console.error('[permits] create failed:', err);
    } finally {
      setPermitCreating(false);
    }
  };

  // Escalation state
  const [escalateModalOpen, setEscalateModalOpen] = useState(false);
  const [escalateDesc, setEscalateDesc] = useState('');
  const [escalateDepts, setEscalateDepts] = useState<string[]>([]);
  const [escalateLoading, setEscalateLoading] = useState(false);

  // Supabase data
  const [crew, setCrew] = useState<any[]>([]);
  const [materials, setMaterials] = useState<any[]>([]);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [photos, setPhotos] = useState<any[]>([]);
  const [milestones, setMilestones] = useState<any[]>([]);
  const [selectedPhotos, setSelectedPhotos] = useState<Set<string>>(new Set());
  const [lightboxPhoto, setLightboxPhoto] = useState<any | null>(null);
  const [dataLoading, setDataLoading] = useState(true);
  // Field-photo (CompanyCam-style) upload state
  const [photosLoading, setPhotosLoading] = useState(false);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ done: number; total: number } | null>(null);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  // Inspection reports
  const [inspections, setInspections] = useState<any[]>([]);
  const [inspectionsLoading, setInspectionsLoading] = useState(false);
  const [creatingInspection, setCreatingInspection] = useState(false);
  const [openInspectionId, setOpenInspectionId] = useState<string | null>(null);
  const [showMeasureTool, setShowMeasureTool] = useState(false);

  // Notes
  const [notes, setNotes] = useState<any[]>([]);
  const [newNoteText, setNewNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  // Documents
  const [documents, setDocuments] = useState<any[]>([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [docUploadSuccess, setDocUploadSuccess] = useState(false);

  // Measurements
  const [measurements, setMeasurements] = useState<any | null>(null);

  // Estimate
  const [estimateForm, setEstimateForm] = useState({
    laborCost: '',
    materialCost: '',
    tearOffCost: '',
    dumpsterCost: '',
    permitCost: '',
    miscCost: '',
    profitMarginPct: '',
    notes: '',
  });
  const [estimateSaving, setEstimateSaving] = useState(false);
  const [estimateSaved, setEstimateSaved] = useState(false);
  const [estimateRecord, setEstimateRecord] = useState<any | null>(null);
  const [estimateSummaryTotals, setEstimateSummaryTotals] = useState<EstimateSummaryTotals>({
    labor: 0, materials: 0, tear_off: 0, dumpster: 0, permit: 0, misc: 0, subtotal: 0, total: 0,
  });
  const [estimateItems, setEstimateItems] = useState<EstimateLineItem[]>([]);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [estimatesList, setEstimatesList] = useState<any[]>([]);
  const [loadingEstimates, setLoadingEstimates] = useState(true);
  const [creatingEstimate, setCreatingEstimate] = useState(false);
  const [estimateError, setEstimateError] = useState<string | null>(null);

  // VPM / QC
  const [vpmName, setVpmName] = useState(card.vpmName ?? '');
  const [qcName, setQcName] = useState(card.qcName ?? '');
  const [showVpmInput, setShowVpmInput] = useState(false);
  const [showQcInput, setShowQcInput] = useState(false);
  const vpmSectionRef = useRef<HTMLDivElement>(null);
  const qcSectionRef = useRef<HTMLDivElement>(null);

  // Close the Sales Rep / Installer pickers when clicking outside them.
  useEffect(() => {
    if (!showVpmInput && !showQcInput) return;
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (showVpmInput && vpmSectionRef.current && !vpmSectionRef.current.contains(t)) {
        setShowVpmInput(false); setVpmSearch(''); setVpmWorkload(null); setHoveredVpmUser(null);
      }
      if (showQcInput && qcSectionRef.current && !qcSectionRef.current.contains(t)) {
        setShowQcInput(false); setQcSearch(''); setQcWorkload(null); setHoveredQcUser(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showVpmInput, showQcInput]);
  const [savingVpm, setSavingVpm] = useState(false);
  const [savingQc, setSavingQc] = useState(false);
  const [vpmUsers, setVpmUsers] = useState<{ id: string; name: string }[]>([]);
  const [qcUsers, setQcUsers] = useState<{ id: string; name: string }[]>([]);
  const [vpmSearch, setVpmSearch] = useState('');
  const [qcSearch, setQcSearch] = useState('');
  const [loadingVpmUsers, setLoadingVpmUsers] = useState(false);
  const [loadingQcUsers, setLoadingQcUsers] = useState(false);

  // Availability calendar state
  const [hoveredVpmUser, setHoveredVpmUser] = useState<{ id: string; name: string } | null>(null);
  const [hoveredQcUser, setHoveredQcUser] = useState<{ id: string; name: string } | null>(null);
  const [vpmWorkload, setVpmWorkload] = useState<{ name: string; count: number } | null>(null);
  const [qcWorkload, setQcWorkload] = useState<{ name: string; count: number } | null>(null);
  const [calendarMonth] = useState<Date>(() => {
    let d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  // Local card stage (so panel reflects stage changes without needing parent re-render)
  const [localStage, setLocalStage] = useState<StageKey>(card.stage);
  const [localColumnEnteredAt, setLocalColumnEnteredAt] = useState(card.columnEnteredAt);
  const [localPaymentSubStatus, setLocalPaymentSubStatus] = useState<PaymentSubStatus>(card.paymentSubStatus);

  // Soft warning state for backward moves
  const [pendingStage, setPendingStage] = useState<StageKey | null>(null);
  const [showBackwardWarning, setShowBackwardWarning] = useState(false);
  const [warningAcknowledged, setWarningAcknowledged] = useState(false);

  // ─── Real-time subscriptions ──────────────────────────────────────────────
  useEffect(() => {
    // Notes subscription
    const notesChannel = supabase
      .channel(`account_notes_${card.id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'account_notes', filter: `pipeline_id=eq.${card.id}` },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            setNotes((prev) => {
              if (prev.some((n) => n.id === (payload.new as any).id)) return prev;
              return [payload.new as any, ...prev];
            });
          } else if (payload.eventType === 'UPDATE') {
            setNotes((prev) =>
              prev.map((n) => n.id === (payload.new as any).id ? payload.new as any : n)
            );
          } else if (payload.eventType === 'DELETE') {
            setNotes((prev) => prev.filter((n) => n.id !== (payload.old as any).id));
          }
        }
      )
      .subscribe();

    // Documents subscription
    const docsChannel = supabase
      .channel(`account_documents_${card.id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'account_documents', filter: `pipeline_id=eq.${card.id}` },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            setDocuments((prev) => {
              if (prev.some((d) => d.id === (payload.new as any).id)) return prev;
              return [payload.new as any, ...prev];
            });
          } else if (payload.eventType === 'UPDATE') {
            setDocuments((prev) =>
              prev.map((d) => d.id === (payload.new as any).id ? payload.new as any : d)
            );
          } else if (payload.eventType === 'DELETE') {
            setDocuments((prev) => prev.filter((d) => d.id !== (payload.old as any).id));
          }
        }
      )
      .subscribe();

    // Measurements subscription
    const measChannel = supabase
      .channel(`roof_measurements_${card.id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'roof_measurements', filter: `pipeline_id=eq.${card.id}` },
        (payload) => {
          if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
            setMeasurements(payload.new as any);
          } else if (payload.eventType === 'DELETE') {
            setMeasurements(null);
          }
        }
      )
      .subscribe();

    // Estimates subscription (customer_pipeline row for this card)
    const estimateChannel = supabase
      .channel(`customer_pipeline_estimate_${card.id}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'customer_pipeline', filter: `id=eq.${card.id}` },
        (payload) => {
          const updated = payload.new as any;
          // Sync stage if changed externally
          if (updated.stage && updated.stage !== localStage) {
            setLocalStage(updated.stage);
            if (updated.column_entered_at) setLocalColumnEnteredAt(updated.column_entered_at);
          }
          // Sync estimate record if present
          if (updated.instant_estimate !== undefined) {
            setEstimateRecord((prev: any) => prev ? { ...prev, instant_estimate: updated.instant_estimate } : prev);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(notesChannel);
      supabase.removeChannel(docsChannel);
      supabase.removeChannel(measChannel);
      supabase.removeChannel(estimateChannel);
    };
  }, [card.id]);

  useEffect(() => {
    async function fetchRelated() {
      try {
        setDataLoading(true);
        const [crewRes, materialsRes, invoicesRes, milestonesRes, notesRes, docsRes, measRes] = await Promise.all([
          supabase.from('crews').select('*').limit(6),
          supabase.from('logistics_jobs').select('*').limit(6),
          supabase.from('invoices').select('*').order('created_at', { ascending: false }).limit(6),
          supabase.from('qc_inspections').select('*').limit(6),
          supabase.from('account_notes').select('*').eq('pipeline_id', card.id).order('created_at', { ascending: false }),
          supabase.from('account_documents').select('*').eq('pipeline_id', card.id).order('created_at', { ascending: false }),
          supabase.from('roof_measurements').select('*').eq('pipeline_id', card.id).order('created_at', { ascending: false }).limit(1),
        ]);
        setCrew(crewRes.data || []);
        setMaterials(materialsRes.data || []);
        setInvoices(invoicesRes.data || []);
        setMilestones((milestonesRes.data || []).map((m: any, i: number) => ({
          id: i + 1,
          label: m.address || `Inspection ${i + 1}`,
          date: m.inspection_date || '—',
          status: (m.status === 'Completed' ? 'completed' : m.status === 'In Progress' ? 'in-progress' : 'upcoming') as 'completed' | 'in-progress' | 'upcoming',
        })));
        setNotes(notesRes.data || []);
        setDocuments(docsRes.data || []);
        setMeasurements(measRes.data?.[0] ?? null);

        // Pre-populate estimate form from card values if available
        setEstimateForm(prev => ({
          ...prev,
          materialCost: card.instantEstimate > 0 ? String(Math.round(card.instantEstimate * 0.45)) : '',
          laborCost: card.instantEstimate > 0 ? String(Math.round(card.instantEstimate * 0.35)) : '',
        }));
      } catch {
        // silently fail
      } finally {
        setDataLoading(false);
      }
    }
    fetchRelated();
  }, [card.id]);

  // --- Field photos (CompanyCam-style) ---------------------------------------
  // Load this customer's photos and mint fresh signed URLs for the private bucket.
  const loadPhotos = useCallback(async () => {
    setPhotosLoading(true);
    try {
      const { data, error } = await supabase
        .from('photos')
        .select('*')
        .eq('pipeline_id', card.id)
        .eq('is_archived', false)
        .order('taken_at', { ascending: false });
      if (error) throw error;
      const rows = data || [];
      const paths = rows.map((r: any) => r.storage_path).filter(Boolean);
      let urlMap: Record<string, string> = {};
      if (paths.length > 0) {
        const res = await fetch('/api/photos/signed-urls', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ paths }),
        });
        if (res.ok) urlMap = (await res.json())?.urls || {};
      }
      setPhotos(
        rows.map((r: any) => ({
          id: r.id,
          path: r.storage_path || null,
          src: r.storage_path ? urlMap[r.storage_path] || '' : r.src || '',
          alt: r.caption || r.alt || 'Job photo',
          caption: r.caption || '',
          gpsLat: r.gps_lat ?? null,
          gpsLng: r.gps_lng ?? null,
          gpsAddress: r.gps_address || null,
          uploadedBy: r.uploaded_by_name || r.crew_member || r.uploaded_by || 'Field Crew',
          uploadedAt: r.taken_at
            ? new Date(r.taken_at).toLocaleString('en-US')
            : r.uploaded_at
              ? new Date(r.uploaded_at).toLocaleString('en-US')
              : '—',
        })),
      );
    } catch {
      setPhotos([]);
    } finally {
      setPhotosLoading(false);
    }
  }, [card.id, supabase]);

  useEffect(() => {
    loadPhotos();
    // Live-refresh when a phone in the field uploads to this customer.
    const channel = supabase
      .channel(`photos-${card.id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'photos', filter: `pipeline_id=eq.${card.id}` },
        () => loadPhotos(),
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [card.id, loadPhotos, supabase]);

  // --- Inspection reports ----------------------------------------------------
  const loadInspections = useCallback(async () => {
    setInspectionsLoading(true);
    try {
      const { data } = await supabase
        .from('inspections')
        .select('id, title, inspection_date, status, updated_at')
        .eq('pipeline_id', card.id)
        .eq('is_archived', false)
        .order('updated_at', { ascending: false });
      setInspections(data || []);
    } catch {
      setInspections([]);
    } finally {
      setInspectionsLoading(false);
    }
  }, [card.id, supabase]);

  useEffect(() => { loadInspections(); }, [loadInspections]);

  const handleNewInspection = async () => {
    setCreatingInspection(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const today = new Date().toISOString().slice(0, 10);
      const { data, error } = await supabase
        .from('inspections')
        .insert({
          pipeline_id: card.id,
          title: 'Roof Inspection',
          inspection_date: today,
          inspector_name: (user?.user_metadata?.full_name as string) || user?.email || null,
          created_by_id: user?.id || null,
          created_by_name: (user?.user_metadata?.full_name as string) || user?.email || null,
          status: 'draft',
        })
        .select();
      if (error) throw error;
      const id = data?.[0]?.id;
      if (id) setOpenInspectionId(id);
    } catch {
      /* ignore */
    } finally {
      setCreatingInspection(false);
    }
  };

  const archiveInspection = async (id: string) => {
    try {
      await supabase.from('inspections').update({ is_archived: true, updated_at: new Date().toISOString() }).eq('id', id);
      setInspections(prev => prev.filter(i => i.id !== id));
    } catch { /* ignore */ }
  };

  const handlePhotoUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const imageFiles = Array.from(files).filter(f => f.type.startsWith('image/'));
    if (imageFiles.length === 0) {
      setPhotoError('No image files selected.');
      return;
    }
    setPhotoError(null);
    setUploadingPhotos(true);
    setUploadProgress({ done: 0, total: imageFiles.length });
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const uploaderName =
        (user?.user_metadata?.full_name as string) || user?.email || 'Field Crew';
      const uploaderId = user?.id || null;

      // Capture the device's GPS location once for this batch (best-effort — a
      // denied prompt or no signal just skips geotagging, never blocks upload).
      let gpsLat: number | null = null;
      let gpsLng: number | null = null;
      let gpsAddress: string | null = null;
      if (typeof navigator !== 'undefined' && navigator.geolocation) {
        try {
          const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              enableHighAccuracy: true,
              timeout: 8000,
              maximumAge: 60000,
            });
          });
          gpsLat = pos.coords.latitude;
          gpsLng = pos.coords.longitude;
          // Reverse-geocode to a readable address (best-effort).
          try {
            const gRes = await fetch('/api/photos/reverse-geocode', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ lat: gpsLat, lng: gpsLng }),
            });
            if (gRes.ok) {
              const g = await gRes.json();
              if (g?.success && g.formatted_address) gpsAddress = g.formatted_address;
            }
          } catch { /* keep coords without address */ }
        } catch { /* location unavailable/denied — upload without GPS */ }
      }

      for (let i = 0; i < imageFiles.length; i++) {
        const file = imageFiles[i];
        const ext = (file.name.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '');
        const rand = typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `${card.id}-${i}-${file.size}`;
        const path = `${card.id}/${rand}.${ext || 'jpg'}`;
        const { error: upErr } = await supabase.storage
          .from('job-photos')
          .upload(path, file, { contentType: file.type, upsert: false });
        if (upErr) throw upErr;
        const { error: insErr } = await supabase.from('photos').insert({
          pipeline_id: card.id,
          job_id: card.jobId || null,
          job_name: `${card.customerFirstName} ${card.customerLastName}`.trim() || null,
          storage_path: path,
          caption: '',
          uploaded_by_id: uploaderId,
          uploaded_by_name: uploaderName,
          taken_at: new Date().toISOString(),
          gps_lat: gpsLat,
          gps_lng: gpsLng,
          gps_address: gpsAddress,
          status: 'pending',
          is_archived: false,
        });
        if (insErr) throw insErr;
        setUploadProgress({ done: i + 1, total: imageFiles.length });
      }
      await loadPhotos();
    } catch (e: any) {
      setPhotoError(e?.message || 'Upload failed. Please try again.');
    } finally {
      setUploadingPhotos(false);
      setUploadProgress(null);
      if (cameraInputRef.current) cameraInputRef.current.value = '';
      if (galleryInputRef.current) galleryInputRef.current.value = '';
    }
  };

  const handleArchivePhotos = async () => {
    if (selectedPhotos.size === 0) return;
    const ids = Array.from(selectedPhotos);
    setPhotoError(null);
    try {
      const { error } = await supabase
        .from('photos')
        .update({ is_archived: true, updated_at: new Date().toISOString() })
        .in('id', ids);
      if (error) throw error;
      setSelectedPhotos(new Set());
      await loadPhotos();
    } catch (e: any) {
      setPhotoError(e?.message || 'Could not remove photos.');
    }
  };

  const handleDial = (number: string) => {
    setDialNotification(`Initiating VoIP call to ${number}...`);
    setTimeout(() => setDialNotification(null), 3000);
  };

  const handleEscalate = async () => {
    if (!escalateDesc.trim() || escalateDepts.length === 0) return;
    setEscalateLoading(true);
    const now = new Date().toISOString();
    const deptList = escalateDepts;
    const jobLabel = card.jobId;
    const customerLabel = `${card.customerFirstName} ${card.customerLastName}`.trim();
    const baseDescription = `${escalateDesc.trim()}\n\nEscalated from Job Card panel.\n\nJob: ${jobLabel}\nCustomer: ${customerLabel}\nAddress: ${card.address}, ${card.city}, ${card.state}\nStage: ${PIPELINE_COLUMNS.find(c => c.id === card.stage)?.title ?? card.stage}`;

    try {
      for (const dept of deptList) {
        const ticketId = `ESC-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`;
        const allDepts = deptList.join(', ');
        const description = deptList.length > 1
          ? `${baseDescription}\n\n[Also sent to: ${allDepts}]`
          : baseDescription;
        await supabase.from('escalations').insert({
          ticket_id: ticketId,
          title: `Job Escalation — ${jobLabel}`,
          description,
          priority: 'high',
          status: 'open',
          source_department: 'Project Management',
          assigned_department: dept,
          affected_job_id: jobLabel,
          created_by_name: 'Staff',
          created_at: now,
          updated_at: now,
          timeline_log: JSON.stringify([{ ts: now, actor: 'Staff', action: 'created', note: `Escalated to ${dept}` }]),
        });
      }
      setEscalateModalOpen(false);
      setEscalateDesc('');
      setEscalateDepts([]);
      setDialNotification(`Escalated to ${deptList.join(', ')}`);
      setTimeout(() => setDialNotification(null), 3500);
    } catch (err: any) {
      console.error('Escalation failed:', err);
    } finally {
      setEscalateLoading(false);
    }
  };

  const handleSaveVpm = async (name: string) => {
    if (!name.trim()) return;
    setSavingVpm(true);
    try {
      await supabase.from('customer_pipeline').update({ vpm_name: name.trim() }).eq('id', card.id);
      setVpmName(name.trim());
      setShowVpmInput(false);
      setVpmSearch('');
    } finally {
      setSavingVpm(false);
    }
  };

  const handleSaveQc = async (name: string) => {
    if (!name.trim()) return;
    setSavingQc(true);
    try {
      await supabase.from('customer_pipeline').update({ qc_name: name.trim() }).eq('id', card.id);
      setQcName(name.trim());
      setShowQcInput(false);
      setQcSearch('');
    } finally {
      setSavingQc(false);
    }
  };

  // Sales Rep picker — anyone with a company account.
  const fetchVpmUsers = async () => {
    setLoadingVpmUsers(true);
    try {
      const { data } = await supabase
        .from('user_profiles')
        .select('id, first_name, last_name')
        .order('first_name');
      if (data) {
        setVpmUsers(
          data
            .map(u => ({ id: u.id, name: `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() }))
            .filter(u => u.name)
        );
      }
    } finally {
      setLoadingVpmUsers(false);
    }
  };

  // Installer picker — any crew onboarded in the system.
  const fetchQcUsers = async () => {
    setLoadingQcUsers(true);
    try {
      const { data } = await supabase
        .from('installer_crews')
        .select('id, company_name, foreman_name')
        .order('company_name');
      if (data) {
        setQcUsers(
          data
            .map((c: any) => ({ id: c.id, name: (c.company_name || c.foreman_name || 'Crew').trim() }))
            .filter((c: any) => c.name)
        );
      }
    } finally {
      setLoadingQcUsers(false);
    }
  };

  const fetchVpmWorkload = async (userName: string) => {
    const { data, error } = await supabase
      .from('customer_pipeline')
      .select('id')
      .eq('vpm_name', userName)
      .neq('id', card.id);
    if (!error) {
      setVpmWorkload({ name: userName, count: data?.length ?? 0 });
    }
  };

  const fetchQcWorkload = async (userName: string) => {
    const { data, error } = await supabase
      .from('customer_pipeline')
      .select('id')
      .eq('qc_name', userName)
      .neq('id', card.id);
    if (!error) {
      setQcWorkload({ name: userName, count: data?.length ?? 0 });
    }
  };

  const handleStageChangeInternal = async (newStage: StageKey) => {
    if (newStage === localStage) return;

    // If backward move, show soft warning first
    if (isBackwardTransition(localStage, newStage) && !warningAcknowledged) {
      setPendingStage(newStage);
      setShowBackwardWarning(true);
      return;
    }

    const now = new Date().toISOString();
    setLocalStage(newStage);
    setLocalColumnEnteredAt(now);
    setWarningAcknowledged(false);
    setPendingStage(null);
    setShowBackwardWarning(false);
    try {
      await supabase.from('customer_pipeline').update({ stage: newStage, updated_at: now, column_entered_at: now }).eq('id', card.id);
    } catch { /* revert handled by parent if needed */ }
    onStageChange?.(card.id, newStage);
  };

  const confirmBackwardMove = async () => {
    if (!pendingStage) return;
    setWarningAcknowledged(true);
    await handleStageChangeInternal(pendingStage);
  };

  const handleSaveNote = async () => {
    if (!newNoteText.trim()) return;
    setSavingNote(true);
    try {
      const { data, error } = await supabase
        .from('account_notes')
        .insert({ pipeline_id: card.id, content: newNoteText.trim(), created_by: 'Staff' })
        .select()
        .single();
      if (!error && data) {
        setNotes(prev => [data, ...prev]);
        setNewNoteText('');
      }
    } finally {
      setSavingNote(false);
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    await supabase.from('account_notes').delete().eq('id', noteId);
    setNotes(prev => prev.filter(n => n.id !== noteId));
  };

  const handleDocUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingDoc(true);
    try {
      const { data, error } = await supabase
        .from('account_documents')
        .insert({
          pipeline_id: card.id,
          file_name: file.name,
          file_url: URL.createObjectURL(file),
          file_type: file.type || 'document',
          file_size_kb: Math.round(file.size / 1024),
          uploaded_by: 'Staff',
        })
        .select()
        .single();
      if (!error && data) {
        setDocuments(prev => [data, ...prev]);
        setDocUploadSuccess(true);
        setTimeout(() => setDocUploadSuccess(false), 3000);
      }
    } finally {
      setUploadingDoc(false);
      e.target.value = '';
    }
  };

  const handleDeleteDoc = async (docId: string) => {
    await supabase.from('account_documents').delete().eq('id', docId);
    setDocuments(prev => prev.filter(d => d.id !== docId));
  };

  const totalInvoiced = invoices.reduce((s: number, inv: any) => s + Number(inv.amount || 0), 0);
  const remaining = card.contractValue - totalInvoiced;

  const foremanData = crew.length > 0 ? {
    name: crew[0].foreman_name || 'Foreman',
    role: 'Lead Foreman',
    phone: crew[0].foreman_phone || '—',
  } : null;

  const crewMembers = crew.slice(1).map((c: any, i: number) => ({
    id: i + 2,
    name: c.foreman_name || `Crew Member ${i + 2}`,
    role: 'Roofer',
    phone: c.foreman_phone || '—',
  }));

  const materialsFormatted = materials.map((m: any, i: number) => ({
    id: i + 1,
    name: m.materials_status || 'Material',
    quantity: '—',
    status: (m.materials_status?.toLowerCase() === 'delivered' ? 'delivered' : 'ordered') as 'delivered' | 'ordered' | 'pending' | 'delayed',
  }));

  const invoicesFormatted = invoices.map((inv: any) => ({
    id: inv.invoice_number || inv.id,
    description: inv.description || 'Invoice',
    amount: Number(inv.amount || 0),
    date: inv.created_at ? new Date(inv.created_at).toLocaleDateString('en-US') : '—',
    status: (inv.status === 'paid' ? 'paid' : 'pending') as 'paid' | 'pending' | 'overdue',
  }));

  const milestoneConfig = {
    completed: { icon: 'CheckCircleIcon', color: 'text-green-600', bg: 'bg-green-100', line: 'bg-green-400' },
    'in-progress': { icon: 'ArrowPathIcon', color: 'text-blue-600', bg: 'bg-blue-100', line: 'bg-gray-200' },
    upcoming: { icon: 'ClockIcon', color: 'text-gray-400', bg: 'bg-gray-100', line: 'bg-gray-200' },
  };

  const materialStatusConfig = {
    delivered: { label: 'Delivered', bg: 'bg-green-100', text: 'text-green-700', icon: 'CheckCircleIcon' },
    pending: { label: 'Pending', bg: 'bg-yellow-100', text: 'text-yellow-700', icon: 'ClockIcon' },
    delayed: { label: 'Delayed', bg: 'bg-red-100', text: 'text-red-700', icon: 'ExclamationTriangleIcon' },
    ordered: { label: 'Ordered', bg: 'bg-blue-100', text: 'text-blue-700', icon: 'TruckIcon' },
  };

  const invoiceStatusConfig = {
    paid: { label: 'Paid', bg: 'bg-green-100', text: 'text-green-700' },
    pending: { label: 'Pending', bg: 'bg-yellow-100', text: 'text-yellow-700' },
    overdue: { label: 'Overdue', bg: 'bg-red-100', text: 'text-red-700' },
  };

  const handleSaveEstimate = async () => {
    setEstimateSaving(true);
    try {
      const labor = parseFloat(estimateForm.laborCost) || 0;
      const material = parseFloat(estimateForm.materialCost) || 0;
      const tearOff = parseFloat(estimateForm.tearOffCost) || 0;
      const dumpster = parseFloat(estimateForm.dumpsterCost) || 0;
      const permit = parseFloat(estimateForm.permitCost) || 0;
      const misc = parseFloat(estimateForm.miscCost) || 0;
      const subtotal = labor + material + tearOff + dumpster + permit + misc;
      const marginPct = parseFloat(estimateForm.profitMarginPct) || 0;
      const total = subtotal * (1 + marginPct / 100);

      const record = {
        pipeline_id: card.id,
        labor_cost: labor,
        material_cost: material,
        tear_off_cost: tearOff,
        dumpster_cost: dumpster,
        permit_cost: permit,
        misc_cost: misc,
        profit_margin_pct: marginPct,
        subtotal,
        total_estimate: total,
        notes: estimateForm.notes,
        created_by: 'Staff',
      };

      setEstimateRecord({ ...record, created_at: new Date().toISOString() });
      setEstimateSaved(true);
      setTimeout(() => setEstimateSaved(false), 3000);
    } finally {
      setEstimateSaving(false);
    }
  };

  // ── Estimates list (launcher for the dedicated builder) ───────────────────
  const loadEstimates = useCallback(async () => {
    setLoadingEstimates(true);
    try {
      const { data } = await supabase
        .from('estimates')
        .select('id, estimate_name, estimate_number, total, status, created_at')
        .eq('pipeline_id', card.id)
        .order('created_at', { ascending: false });
      setEstimatesList(data ?? []);
    } finally {
      setLoadingEstimates(false);
    }
  }, [card.id, supabase]);

  useEffect(() => { loadEstimates(); }, [loadEstimates]);

  const handleNewEstimate = async () => {
    setCreatingEstimate(true);
    setEstimateError(null);
    try {
      const { data, error } = await supabase.from('estimates').insert({
        pipeline_id: card.id,
        estimate_number: card.jobId,
        estimate_name: `Estimate ${card.jobId || ''}`.trim(),
        customer_first_name: card.customerFirstName || '',
        customer_last_name: card.customerLastName || '',
        email: card.email || '',
        phone: card.phone || '',
        address: card.address || '',
        city: card.city || '',
        state: card.state || '',
        zip: card.zip || '',
        status: 'draft',
      }).select('id');
      if (error) throw error;
      const newId = data?.[0]?.id;
      if (newId) { router.push(`/estimates/${newId}`); return; }
    } catch (e: any) {
      const msg = e?.message || e?.details || e?.hint || e?.code || 'Insert failed';
      console.error('[New Estimate] failed:', msg, e);
      setEstimateError(msg);
    }
    setCreatingEstimate(false);
  };

  // ── Estimate → PDF (Phase 1) ──────────────────────────────────────────────
  const buildEstimatePdfData = () => {
    const items = estimateItems.filter(i => i.line_total > 0);
    const subtotal = items.reduce((s, i) => s + i.line_total, 0);
    const total = subtotal;
    const deposit = Math.round(total * 0.30 * 100) / 100;
    return {
      jobId: card.jobId,
      optionLabel: 'Estimate',
      date: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      logoSrc: typeof window !== 'undefined' ? `${window.location.origin}/assets/images/nextroof-logo.png` : undefined,
      customer: {
        firstName: card.customerFirstName || '',
        lastName: card.customerLastName || '',
        address: card.address || '',
        city: card.city || '',
        state: card.state || '',
        zip: card.zip || '',
        email: card.email || '',
        phone: card.phone || '',
      },
      lineItems: items.map(i => ({ section: i.section, item_name: i.item_name, line_total: i.line_total })),
      subtotal, total, deposit,
    };
  };

  const handleDownloadPdf = async () => {
    setPdfBusy(true);
    try {
      const { downloadEstimatePdf } = await import('@/components/common/EstimatePDF');
      await downloadEstimatePdf(buildEstimatePdfData() as any);
    } catch (e) {
      console.error('[Estimate PDF]', e);
    } finally {
      setPdfBusy(false);
    }
  };

  const handleSaveEstimateToDb = async () => {
    setEstimateSaving(true);
    try {
      const d = buildEstimatePdfData();
      const { error } = await supabase.from('estimates').insert({
        pipeline_id: card.id,
        estimate_number: card.jobId,
        option_label: d.optionLabel,
        customer_first_name: d.customer.firstName,
        customer_last_name: d.customer.lastName,
        email: d.customer.email,
        phone: d.customer.phone,
        address: d.customer.address,
        city: d.customer.city,
        state: d.customer.state,
        zip: d.customer.zip,
        line_items: d.lineItems,
        subtotal: d.subtotal,
        total: d.total,
        deposit: d.deposit,
        notes: estimateForm.notes,
        status: 'draft',
      });
      if (error) throw error;
      setEstimateSaved(true);
      setTimeout(() => setEstimateSaved(false), 3000);
    } catch (e) {
      console.error('[Estimate Save]', e);
    } finally {
      setEstimateSaving(false);
    }
  };

  const estimateSubtotal =
    (parseFloat(estimateForm.laborCost) || 0) +
    (parseFloat(estimateForm.materialCost) || 0) +
    (parseFloat(estimateForm.tearOffCost) || 0) +
    (parseFloat(estimateForm.dumpsterCost) || 0) +
    (parseFloat(estimateForm.permitCost) || 0) +
    (parseFloat(estimateForm.miscCost) || 0);
  const estimateMargin = parseFloat(estimateForm.profitMarginPct) || 0;
  const estimateTotal = estimateSubtotal * (1 + estimateMargin / 100);

  const fullName = `${card.customerFirstName} ${card.customerLastName}`.trim();
  const fullAddress = [card.address, card.city, card.state, card.zip].filter(Boolean).join(', ');
  const initials = `${card.customerFirstName?.charAt(0) ?? ''}${card.customerLastName?.charAt(0) ?? ''}`.toUpperCase();
  const colColor = PIPELINE_COLUMNS.find(c => c.id === localStage)?.color ?? '#6366F1';
  const days = getDaysInColumn(localStage === card.stage ? card.columnEnteredAt : localColumnEnteredAt);
  const { bg: daysBg, text: daysText } = getDaysColor(days);

  return (
    <>
      {/* Full-screen job view (JobNimbus-style: left tab rail + main column) */}
      <div className="fixed inset-0 z-[120] bg-white flex">
        {/* ── Left tab rail (desktop only) ── */}
        <aside className="hidden md:flex w-60 flex-shrink-0 flex-col" style={{ background: '#1C2E5E' }}>
          <div className="px-4 py-4 border-b border-white/10">
            <p className="text-white font-bold text-[15px] leading-tight truncate">{fullName}</p>
            <p className="text-white/50 text-xs font-mono mt-0.5">#{card.jobId}</p>
          </div>
          <nav className="flex-1 overflow-y-auto py-2">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors border-l-[3px] ${
                  activeTab === tab.id
                    ? 'bg-white/10 text-white border-[#E89A3C]'
                    : 'text-white/60 hover:text-white hover:bg-white/5 border-transparent'
                }`}
              >
                <Icon name={tab.icon as any} size={16} variant={activeTab === tab.id ? 'solid' : 'outline'} />
                {tab.label}
              </button>
            ))}
          </nav>
        </aside>

        {/* ── Main column ── */}
        <div className="flex-1 flex flex-col min-w-0 bg-white">
          {/* Mobile tab strip (replaces the left rail on small screens) */}
          <div className="md:hidden flex-shrink-0 overflow-x-auto border-b border-white/10" style={{ background: '#1C2E5E' }}>
            <div className="flex items-center gap-1 px-2 py-2 min-w-max">
              {TABS.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                    activeTab === tab.id ? 'bg-white text-[#1C2E5E]' : 'text-white/70'
                  }`}
                >
                  <Icon name={tab.icon as any} size={14} variant={activeTab === tab.id ? 'solid' : 'outline'} />
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Dial notification */}
          {dialNotification && (
            <div className="absolute top-4 right-4 z-10 flex items-center gap-2 bg-green-500 text-white px-4 py-2.5 rounded-lg shadow-lg text-sm font-medium">
              <Icon name="PhoneIcon" size={15} variant="solid" />
              {dialNotification}
            </div>
          )}

          {/* ── Header ── */}
          <div className="bg-white border-b border-gray-100 px-4 md:px-6 py-5 flex-shrink-0">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="flex items-center gap-3 mb-1">
                  <span className="text-xs text-gray-400 font-mono">#{card.jobId}</span>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 capitalize">
                    {PIPELINE_COLUMNS.find(c => c.id === localStage)?.title ?? localStage}
                  </span>
                </div>
                <h1 className="font-heading text-2xl font-bold" style={{ color: '#1C2E5E' }}>{fullName} Residence</h1>
                <div className="flex items-center gap-1.5 mt-1 text-gray-400">
                  <Icon name="MapPinIcon" size={14} />
                  <span className="text-sm">{fullAddress}</span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <p className="text-xs text-gray-400">Days in Stage</p>
                  <span className="text-sm font-bold px-3 py-1 rounded-full" style={{ background: daysBg, color: daysText }}>{days}d</span>
                </div>
                <button onClick={onClose} className="w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors flex-shrink-0" aria-label="Close panel">
                  <Icon name="XMarkIcon" size={18} className="text-gray-500" />
                </button>
              </div>
            </div>
            {/* Completion bar */}
            <div className="mt-4">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs text-gray-400">Overall Completion</span>
                <span className="text-xs font-medium text-gray-600">
                  {Math.round(((STAGE_ORDER.indexOf(localStage) + 1) / STAGE_ORDER.length) * 100)}%
                </span>
              </div>
              <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${Math.round(((STAGE_ORDER.indexOf(localStage) + 1) / STAGE_ORDER.length) * 100)}%`,
                    background: 'linear-gradient(90deg, #E89A3C, #1C2E5E)',
                  }}
                />
              </div>
            </div>
          </div>

          {/* ── Action Bar ── */}
          <div className="flex items-center justify-between gap-2 px-4 md:px-6 py-3 border-b border-gray-100 bg-gray-50 flex-shrink-0 overflow-x-auto">
            <div className="flex items-center gap-2 flex-shrink-0">
              <button onClick={() => setActiveTab('photos')} className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap">
                <Icon name="PhotoIcon" size={15} className="text-blue-500" />
                <span className="hidden sm:inline">Photo Gallery</span>
              </button>
              <Link href="/interactive-calendar-schedule-management" className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap">
                <Icon name="CalendarDaysIcon" size={15} className="text-blue-500" />
                <span className="hidden sm:inline">Schedule</span>
              </Link>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => setShowEditCustomer(true)}
                className="flex items-center gap-2 px-3 md:px-4 py-2.5 rounded-lg text-sm font-semibold shadow-sm border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap"
              >
                <Icon name="PencilSquareIcon" size={16} />
                <span className="hidden sm:inline">Edit Customer</span>
              </button>
              <button
                onClick={() => { setEscalateDesc(''); setEscalateDepts([]); setEscalateModalOpen(true); }}
                className="flex items-center gap-2 px-3 md:px-4 py-2.5 rounded-lg text-sm font-semibold text-white shadow-sm bg-red-600 hover:bg-red-700 transition-colors whitespace-nowrap"
              >
                <Icon name="ExclamationTriangleIcon" size={16} variant="solid" />
                <span className="hidden sm:inline">Escalate</span>
              </button>
              <button
                onClick={() => setShowChangeOrder(true)}
                className="flex items-center gap-2 px-3 md:px-4 py-2.5 rounded-lg text-sm font-semibold text-white shadow-sm hover:opacity-90 transition-opacity whitespace-nowrap"
                style={{ backgroundColor: '#1C2E5E' }}
              >
                <Icon name="PlusCircleIcon" size={16} variant="solid" />
                <span className="hidden sm:inline">Create Change Order</span>
              </button>
            </div>
          </div>

          {/* ── Tab Content ── */}
          <div className="flex-1 overflow-y-auto p-4 md:p-6">
            {/* Stage selector */}
            <div className="mb-5 flex items-center gap-3 flex-wrap">
              <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Move to Stage:</label>
              <select
                value={localStage}
                onChange={e => handleStageChangeInternal(e.target.value as StageKey)}
                className="text-sm font-medium rounded-lg border border-gray-200 px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-300 bg-white min-w-fit"
                style={{ color: '#1C2E5E', width: 'max-content', maxWidth: '100%' }}
              >
                <option value={localStage} disabled>
                  {PIPELINE_COLUMNS.find(c => c.id === localStage)?.title ?? localStage} (current)
                </option>
                {getValidNextStages(localStage).map(stageKey => {
                  const isBackward = isBackwardTransition(localStage, stageKey);
                  const label = PIPELINE_COLUMNS.find(c => c.id === stageKey)?.title ?? stageKey;
                  return (
                    <option key={stageKey} value={stageKey}>
                      {isBackward ? `↩ ${label}` : label}
                    </option>
                  );
                })}
              </select>
              {getValidNextStages(localStage).length === 0 && (
                <span className="text-xs text-gray-400 italic">Final stage reached</span>
              )}
            </div>

            {/* OVERVIEW TAB */}
            {activeTab === 'overview' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <h3 className="font-heading text-base font-semibold flex items-center gap-2" style={{ color: '#1C2E5E' }}>
                    <Icon name="UserCircleIcon" size={18} variant="solid" className="text-blue-500" />
                    Homeowner Information
                  </h3>
                  <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 text-white text-sm font-bold" style={{ background: `linear-gradient(135deg, ${colColor}, #E89A3C)` }}>
                        {initials}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-800">{fullName}</p>
                        <p className="text-xs text-gray-400">Primary Contact</p>
                      </div>
                    </div>
                    <div className="space-y-2 pt-1">
                      <div className="flex items-start gap-2.5">
                        <Icon name="MapPinIcon" size={15} className="text-gray-400 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-gray-700">{fullAddress}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2.5">
                          <Icon name="PhoneIcon" size={15} className="text-gray-400 flex-shrink-0" />
                          <span className="text-sm text-gray-700">{card.phone || '—'}</span>
                        </div>
                        {card.phone && (
                          <div className="flex items-center gap-1.5">
                            <button onClick={() => handleDial(card.phone)} className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium text-white" style={{ backgroundColor: '#E89A3C' }}>
                              <Icon name="PhoneIcon" size={11} variant="solid" />
                              Dial
                            </button>
                            <a href={`sms:${card.phone}`} className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium text-white" style={{ backgroundColor: '#10B981' }}>
                              <Icon name="ChatBubbleLeftEllipsisIcon" size={11} variant="solid" />
                              Message
                            </a>
                            <a href={`sms:${card.phone}`} className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium text-white" style={{ backgroundColor: '#6366F1' }}>
                              <Icon name="ChatBubbleBottomCenterTextIcon" size={11} variant="solid" />
                              Text
                            </a>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2.5">
                        <Icon name="EnvelopeIcon" size={15} className="text-gray-400 flex-shrink-0" />
                        <span className="text-sm text-gray-700">{card.email || '—'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Sales Rep Assignment */}
                  <div ref={vpmSectionRef} className="bg-gray-50 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Icon name="BriefcaseIcon" size={15} className="text-blue-500 flex-shrink-0" />
                        <span className="text-sm font-semibold text-gray-700">Sales Rep</span>
                      </div>
                      {vpmName ? (
                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-1.5">
                            <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 text-xs font-bold flex-shrink-0">
                              {vpmName.charAt(0).toUpperCase()}
                            </div>
                            <span className="text-sm text-gray-800 font-medium">{vpmName}</span>
                          </div>
                          <button onClick={() => { setVpmSearch(''); setVpmWorkload(null); setHoveredVpmUser(null); fetchVpmUsers(); setShowVpmInput(true); }} className="p-1 rounded hover:bg-gray-200 transition-colors" aria-label="Edit Sales Rep">
                            <Icon name="PencilSquareIcon" size={13} className="text-gray-400" />
                          </button>
                        </div>
                      ) : (
                        <button onClick={() => { setVpmSearch(''); setVpmWorkload(null); setHoveredVpmUser(null); fetchVpmUsers(); setShowVpmInput(true); }} className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-blue-600 hover:bg-blue-50 transition-colors border border-blue-200" aria-label="Assign Sales Rep">
                          <Icon name="PlusIcon" size={13} variant="solid" />
                          Assign
                        </button>
                      )}
                    </div>
                    {showVpmInput && (
                      <div className="mt-3">
                        <input
                          type="text"
                          value={vpmSearch}
                          onChange={e => setVpmSearch(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Escape') { setShowVpmInput(false); setVpmSearch(''); setVpmWorkload(null); setHoveredVpmUser(null); } }}
                          placeholder="Search company users..."
                          autoFocus
                          className="w-full px-3 py-1.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                        />
                        <div className="mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-40 overflow-y-auto z-10">
                          {loadingVpmUsers ? (
                            <div className="px-3 py-2 text-xs text-gray-400">Loading...</div>
                          ) : (() => {
                            const filtered = vpmUsers.filter(u => u.name.toLowerCase().includes(vpmSearch.toLowerCase()));
                            return filtered.length === 0 ? (
                              <div className="px-3 py-2 text-xs text-gray-400">
                                {vpmUsers.length === 0 ? 'No users found' : 'No matches'}
                              </div>
                            ) : filtered.map(u => (
                              <button
                                key={u.id}
                                onMouseEnter={() => { setHoveredVpmUser(u); fetchVpmWorkload(u.name); }}
                                onMouseLeave={() => { setHoveredVpmUser(null); setVpmWorkload(null); }}
                                onClick={() => { handleSaveVpm(u.name); setVpmWorkload(null); setHoveredVpmUser(null); }}
                                disabled={savingVpm}
                                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors flex items-center gap-2"
                              >
                                <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 text-xs font-bold flex-shrink-0">
                                  {u.name.charAt(0).toUpperCase()}
                                </div>
                                {u.name}
                              </button>
                            ));
                          })()}
                        </div>
                        {/* Mini Availability Calendar for VPM */}
                        {(hoveredVpmUser || vpmWorkload) && (
                          <MiniAvailabilityCalendar
                            userName={(hoveredVpmUser?.name ?? vpmWorkload?.name) ?? ''}
                            assignmentCount={vpmWorkload?.count ?? null}
                            accentColor="blue"
                            calendarMonth={calendarMonth}
                          />
                        )}
                        <button onClick={() => { setShowVpmInput(false); setVpmSearch(''); setVpmWorkload(null); setHoveredVpmUser(null); }} className="mt-1 text-xs text-gray-400 hover:text-gray-600 transition-colors">Cancel</button>
                      </div>
                    )}
                  </div>

                  {/* Installer Assignment */}
                  <div ref={qcSectionRef} className="bg-gray-50 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Icon name="WrenchScrewdriverIcon" size={15} className="text-green-500 flex-shrink-0" />
                        <span className="text-sm font-semibold text-gray-700">Installer</span>
                      </div>
                      {qcName ? (
                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-1.5">
                            <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center text-green-700 text-xs font-bold flex-shrink-0">
                              {qcName.charAt(0).toUpperCase()}
                            </div>
                            <span className="text-sm text-gray-800 font-medium">{qcName}</span>
                          </div>
                          <button onClick={() => { setQcSearch(''); setQcWorkload(null); setHoveredQcUser(null); fetchQcUsers(); setShowQcInput(true); }} className="p-1 rounded hover:bg-gray-200 transition-colors" aria-label="Edit Installer">
                            <Icon name="PencilSquareIcon" size={13} className="text-gray-400" />
                          </button>
                        </div>
                      ) : (
                        <button onClick={() => { setQcSearch(''); setQcWorkload(null); setHoveredQcUser(null); fetchQcUsers(); setShowQcInput(true); }} className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-green-600 hover:bg-green-50 transition-colors border border-green-200" aria-label="Assign Installer">
                          <Icon name="PlusIcon" size={13} variant="solid" />
                          Assign
                        </button>
                      )}
                    </div>
                    {showQcInput && (
                      <div className="mt-3">
                        <input
                          type="text"
                          value={qcSearch}
                          onChange={e => setQcSearch(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Escape') { setShowQcInput(false); setQcSearch(''); setQcWorkload(null); setHoveredQcUser(null); } }}
                          placeholder="Search onboarded crews..."
                          autoFocus
                          className="w-full px-3 py-1.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-green-300"
                        />
                        <div className="mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-40 overflow-y-auto z-10">
                          {loadingQcUsers ? (
                            <div className="px-3 py-2 text-xs text-gray-400">Loading...</div>
                          ) : (() => {
                            const filtered = qcUsers.filter(u => u.name.toLowerCase().includes(qcSearch.toLowerCase()));
                            return filtered.length === 0 ? (
                              <div className="px-3 py-2 text-xs text-gray-400">
                                {qcUsers.length === 0 ? 'No crews onboarded yet' : 'No matches'}
                              </div>
                            ) : filtered.map(u => (
                              <button
                                key={u.id}
                                onMouseEnter={() => { setHoveredQcUser(u); fetchQcWorkload(u.name); }}
                                onMouseLeave={() => { setHoveredQcUser(null); setQcWorkload(null); }}
                                onClick={() => { handleSaveQc(u.name); setQcWorkload(null); setHoveredQcUser(null); }}
                                disabled={savingQc}
                                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-green-50 hover:text-green-700 transition-colors flex items-center gap-2"
                              >
                                <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center text-green-700 text-xs font-bold flex-shrink-0">
                                  {u.name.charAt(0).toUpperCase()}
                                </div>
                                {u.name}
                              </button>
                            ));
                          })()}
                        </div>
                        {/* Mini Availability Calendar for QC */}
                        {(hoveredQcUser || qcWorkload) && (
                          <MiniAvailabilityCalendar
                            userName={(hoveredQcUser?.name ?? qcWorkload?.name) ?? ''}
                            assignmentCount={qcWorkload?.count ?? null}
                            accentColor="green"
                            calendarMonth={calendarMonth}
                          />
                        )}
                        <button onClick={() => { setShowQcInput(false); setQcSearch(''); setQcWorkload(null); setHoveredQcUser(null); }} className="mt-1 text-xs text-gray-400 hover:text-gray-600 transition-colors">Cancel</button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Financial Summary + Quick Actions */}
                <div className="space-y-6">
                  <div>
                    <h3 className="font-heading text-base font-semibold flex items-center gap-2 mb-3" style={{ color: '#1C2E5E' }}>
                      <Icon name="BanknotesIcon" size={18} variant="solid" className="text-blue-500" />
                      Financial Summary
                    </h3>
                    <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                      {[
                        { label: 'Contract Value', value: card.contractValue > 0 ? formatCurrency(card.contractValue) : '—' },
                        { label: 'Instant Estimate', value: card.instantEstimate > 0 ? formatCurrency(card.instantEstimate) : '—' },
                        { label: 'Invoiced to Date', value: totalInvoiced > 0 ? formatCurrency(totalInvoiced) : '—' },
                        { label: 'Remaining Balance', value: card.contractValue > 0 ? formatCurrency(remaining) : '—' },
                        { label: 'Job Status', value: PIPELINE_COLUMNS.find(c => c.id === localStage)?.title ?? localStage, highlight: true },
                      ].map((item: any) => (
                        <div key={item.label} className="flex items-center justify-between">
                          <span className="text-sm text-gray-400">{item.label}</span>
                          <span className={`text-sm font-medium ${item.highlight ? 'text-green-600' : 'text-gray-800'}`}>{item.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="font-heading text-base font-semibold flex items-center gap-2 mb-3" style={{ color: '#1C2E5E' }}>
                      <Icon name="HomeIcon" size={18} variant="solid" className="text-blue-500" />
                      Roof &amp; Estimate Details
                    </h3>
                    <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                      {[
                        { label: 'Squares', value: card.totalSquares > 0 ? `${card.totalSquares}` : '—' },
                        { label: 'Material', value: card.material || '—' },
                        { label: 'Color', value: card.shingleColor || '—' },
                        { label: '$ / Square', value: card.costPerSquare != null ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(card.costPerSquare) : '—' },
                      ].map((item) => (
                        <div key={item.label} className="flex items-center justify-between">
                          <span className="text-sm text-gray-400">{item.label}</span>
                          <span className="text-sm font-medium text-gray-800">{item.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="font-heading text-base font-semibold flex items-center gap-2 mb-3" style={{ color: '#1C2E5E' }}>
                      <Icon name="BoltIcon" size={18} variant="solid" className="text-blue-500" />
                      Quick Actions
                    </h3>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { label: 'Log Communication', icon: 'ChatBubbleLeftRightIcon', href: '/virtual-meetings-customer-communication-hub' },
                        { label: 'View Schedule', icon: 'CalendarDaysIcon', href: '/interactive-calendar-schedule-management' },
                        { label: 'Upload Photos', icon: 'CameraIcon', tab: 'photos' as TabId },
                        { label: 'Role Management', icon: 'UsersIcon', href: '/role-based-navigation-module-management' },
                      ].map(action => (
                        action.tab ? (
                          <button key={action.label} onClick={() => setActiveTab(action.tab as TabId)} className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 transition-colors text-left">
                            <Icon name={action.icon as any} size={15} className="text-blue-500 flex-shrink-0" />
                            <span className="text-xs font-medium text-gray-700">{action.label}</span>
                          </button>
                        ) : (
                          <Link key={action.label} href={action.href as string} className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 transition-colors">
                            <Icon name={action.icon as any} size={15} className="text-blue-500 flex-shrink-0" />
                            <span className="text-xs font-medium text-gray-700">{action.label}</span>
                          </Link>
                        )
                      ))}
                    </div>
                  </div>
                </div>

                {/* Customer Details — everything entered on the New Customer form */}
                {(() => {
                  const dash = (v: any) => (v == null || v === '' ? '—' : String(v));
                  const yesNo = (v: boolean | null) => (v == null ? '—' : v ? 'Yes' : 'No');
                  const groups: { title: string; icon: any; rows: { label: string; value: string }[] }[] = [
                    {
                      title: 'Contact',
                      icon: 'IdentificationIcon',
                      rows: [
                        { label: 'Display Name', value: dash(card.displayName) },
                        { label: 'Company', value: dash(card.company) },
                        { label: 'Contact Type', value: dash(card.contactType) },
                        { label: 'Phone Type', value: dash(card.phoneType) },
                        { label: 'Phone Ext', value: dash(card.phoneExt) },
                        { label: 'Website', value: dash(card.website) },
                      ],
                    },
                    {
                      title: 'Job Details',
                      icon: 'BriefcaseIcon',
                      rows: [
                        { label: 'Job Type', value: dash(card.jobType) },
                        { label: 'Location', value: dash(card.location) },
                        { label: 'Assigned To', value: dash(card.assignedTo) },
                        { label: 'Lead Source', value: dash(card.referralSource) },
                        { label: 'Scheduled Date', value: dash(card.jobScheduledDate) },
                        { label: 'Scheduled Time', value: dash(card.jobTime) },
                        { label: 'SalesRabbit ID', value: dash(card.salesrabbitId) },
                      ],
                    },
                    {
                      title: 'Address',
                      icon: 'MapPinIcon',
                      rows: [
                        { label: 'Address Line 2', value: dash(card.addressLine2) },
                        { label: 'Country', value: dash(card.country) },
                        { label: 'Gated Community', value: yesNo(card.gatedCommunity) },
                        { label: 'Gate Code', value: dash(card.gateCode) },
                      ],
                    },
                    {
                      title: 'Insurance & Adjuster',
                      icon: 'ShieldCheckIcon',
                      rows: [
                        { label: 'Date of Loss', value: dash(card.dateOfLoss) },
                        { label: 'Insurance Company', value: dash(card.insuranceCompany) },
                        { label: 'Claim #', value: dash(card.claimNumber) },
                        { label: 'Policy #', value: dash(card.policyNumber) },
                        { label: 'Adjuster', value: dash(card.adjusterName) },
                        { label: 'Adjuster Phone', value: dash(card.adjusterPhone) },
                        { label: 'Adjuster Email', value: dash(card.adjusterEmail) },
                        { label: 'Outside Adjuster', value: dash(card.outsideAdjuster) },
                        { label: 'Outside Adj. Email', value: dash(card.outsideAdjusterEmail) },
                        { label: 'Outside Adj. Phone', value: dash(card.outsideAdjusterNumber) },
                      ],
                    },
                  ];
                  return (
                    <div className="lg:col-span-2 space-y-4">
                      <div className="flex items-center justify-between">
                        <h3 className="font-heading text-base font-semibold flex items-center gap-2" style={{ color: '#1C2E5E' }}>
                          <Icon name="ClipboardDocumentListIcon" size={18} variant="solid" className="text-blue-500" />
                          Customer Details
                        </h3>
                        <button
                          onClick={() => setShowEditCustomer(true)}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 transition-colors"
                        >
                          <Icon name="PencilSquareIcon" size={13} />
                          Edit
                        </button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {groups.map(g => (
                          <div key={g.title} className="bg-gray-50 rounded-xl p-4">
                            <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3 flex items-center gap-1.5">
                              <Icon name={g.icon} size={13} className="text-blue-400" />
                              {g.title}
                            </p>
                            <div className="space-y-2">
                              {g.rows.map(r => (
                                <div key={r.label} className="flex items-start justify-between gap-3">
                                  <span className="text-sm text-gray-400 flex-shrink-0">{r.label}</span>
                                  <span className="text-sm font-medium text-gray-800 text-right break-words">{r.value}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* CREW TAB */}
            {activeTab === 'crew' && (
              <div className="space-y-4">
                <h3 className="font-heading text-base font-semibold flex items-center gap-2" style={{ color: '#1C2E5E' }}>
                  <Icon name="UsersIcon" size={18} variant="solid" className="text-blue-500" />
                  Crew Information
                </h3>
                {dataLoading ? (
                  <div className="flex items-center justify-center py-10">
                    <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : foremanData ? (
                  <div className="bg-gray-50 rounded-xl p-4 space-y-4">
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">Foreman</p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-sm flex-shrink-0">
                            {foremanData.name.charAt(0)}
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-gray-800">{foremanData.name}</p>
                            <p className="text-xs text-gray-400">{foremanData.role}</p>
                          </div>
                        </div>
                        <button onClick={() => handleDial(foremanData.phone)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium text-white" style={{ backgroundColor: '#E89A3C' }}>
                          <Icon name="PhoneIcon" size={12} variant="solid" />
                          {foremanData.phone}
                        </button>
                      </div>
                    </div>
                    {crewMembers.length > 0 && (
                      <div>
                        <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">Team ({crewMembers.length} members)</p>
                        <div className="space-y-2">
                          {crewMembers.map((m: any) => (
                            <div key={m.id} className="flex items-center justify-between">
                              <div className="flex items-center gap-2.5">
                                <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 text-xs font-bold flex-shrink-0">
                                  {m.name.charAt(0)}
                                </div>
                                <div>
                                  <p className="text-sm text-gray-800">{m.name}</p>
                                  <p className="text-xs text-gray-400">{m.role}</p>
                                </div>
                              </div>
                              <button onClick={() => handleDial(m.phone)} className="text-blue-500 hover:text-blue-700 transition-colors" aria-label={`Call ${m.name}`}>
                                <Icon name="PhoneIcon" size={15} />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <Icon name="UsersIcon" size={36} className="text-gray-300 mb-2" />
                    <p className="text-sm text-gray-400">No crew assigned to this job yet.</p>
                  </div>
                )}
              </div>
            )}

            {/* LOGISTICS TAB */}
            {activeTab === 'logistics' && (
              <div className="space-y-5">
                <h3 className="font-heading text-base font-semibold flex items-center gap-2" style={{ color: '#1C2E5E' }}>
                  <Icon name="TruckIcon" size={18} variant="solid" className="text-blue-500" />
                  Job &amp; Logistics
                </h3>
                {dataLoading ? (
                  <div className="flex items-center justify-center py-10">
                    <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : (
                  <>
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">Material Orders</p>
                      {materialsFormatted.length === 0 ? (
                        <p className="text-sm text-gray-400 py-4 text-center">No material orders found.</p>
                      ) : (
                        <div className="space-y-2">
                          {materialsFormatted.map(mat => {
                            const cfg = materialStatusConfig[mat.status];
                            return (
                              <div key={mat.id} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2.5">
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm text-gray-700 truncate">{mat.name}</p>
                                  <p className="text-xs text-gray-400">{mat.quantity}</p>
                                </div>
                                <span className={`ml-2 flex-shrink-0 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.bg} ${cfg.text}`}>{cfg.label}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">Invoices</p>
                      {invoicesFormatted.length === 0 ? (
                        <p className="text-sm text-gray-400 py-4 text-center">No invoices found.</p>
                      ) : (
                        <div className="space-y-2">
                          {invoicesFormatted.map(inv => {
                            const cfg = invoiceStatusConfig[inv.status];
                            return (
                              <div key={inv.id} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2.5">
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm text-gray-700 truncate">{inv.description}</p>
                                  <p className="text-xs text-gray-400">{inv.date} · <span className="font-mono">${inv.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span></p>
                                </div>
                                <span className={`ml-2 flex-shrink-0 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.bg} ${cfg.text}`}>{cfg.label}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* PHOTOS TAB */}
            {activeTab === 'photos' && (
              <div className="space-y-4">
                {/* Hidden inputs: camera opens the rear camera on phones; gallery
                    lets you pick existing photos / desktop files. */}
                <input
                  ref={cameraInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  multiple
                  className="hidden"
                  onChange={e => handlePhotoUpload(e.target.files)}
                />
                <input
                  ref={galleryInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={e => handlePhotoUpload(e.target.files)}
                />

                <div className="flex items-center justify-between flex-wrap gap-2">
                  <h3 className="font-heading text-base font-semibold flex items-center gap-2" style={{ color: '#1C2E5E' }}>
                    <Icon name="PhotoIcon" size={18} variant="solid" className="text-blue-500" />
                    Field Photos
                    {photos.length > 0 && (
                      <span className="text-xs font-normal text-gray-400">({photos.length})</span>
                    )}
                  </h3>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => cameraInputRef.current?.click()}
                      disabled={uploadingPhotos}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold text-white shadow-sm disabled:opacity-60"
                      style={{ backgroundColor: '#E89A3C' }}
                    >
                      <Icon name="CameraIcon" size={15} variant="solid" />
                      Take Photo
                    </button>
                    <button
                      onClick={() => galleryInputRef.current?.click()}
                      disabled={uploadingPhotos}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold text-white shadow-sm disabled:opacity-60"
                      style={{ backgroundColor: '#1C2E5E' }}
                    >
                      <Icon name="ArrowUpTrayIcon" size={15} variant="solid" />
                      Upload
                    </button>
                  </div>
                </div>

                {photoError && (
                  <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5">
                    <Icon name="ExclamationTriangleIcon" size={16} variant="solid" className="text-red-600" />
                    <span className="text-sm text-red-700">{photoError}</span>
                  </div>
                )}

                {uploadingPhotos && (
                  <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2.5">
                    <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                    <span className="text-sm text-blue-700">
                      Uploading{uploadProgress ? ` ${uploadProgress.done}/${uploadProgress.total}` : '…'}
                    </span>
                  </div>
                )}

                {photos.length > 0 && (
                  <div className="flex items-center gap-2">
                    <button onClick={() => setSelectedPhotos(new Set(photos.map((p: any) => p.id)))} className="text-xs text-blue-500 hover:text-blue-700 transition-colors">Select All</button>
                    <span className="text-gray-200">|</span>
                    <button onClick={() => setSelectedPhotos(new Set())} className="text-xs text-gray-400 hover:text-gray-600 transition-colors">Clear</button>
                  </div>
                )}

                {photosLoading ? (
                  <div className="flex items-center justify-center py-10">
                    <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : photos.length === 0 ? (
                  <button
                    onClick={() => cameraInputRef.current?.click()}
                    className="w-full flex flex-col items-center justify-center py-12 text-center border-2 border-dashed border-gray-200 rounded-xl hover:border-blue-300 hover:bg-blue-50/40 transition-colors"
                  >
                    <Icon name="CameraIcon" size={36} className="text-gray-300 mb-2" />
                    <p className="text-sm font-medium text-gray-500">No photos yet</p>
                    <p className="text-xs text-gray-400 mt-0.5">Tap to snap a job-site photo — it uploads straight to this customer.</p>
                  </button>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {photos.map((photo: any) => (
                      <div key={photo.id} className={`relative rounded-lg overflow-hidden border-2 transition-all cursor-pointer group ${selectedPhotos.has(photo.id) ? 'border-blue-500' : 'border-transparent'}`}>
                        <div className="aspect-square overflow-hidden bg-gray-100" onClick={() => setLightboxPhoto(photo)}>
                          <AppImage src={photo.src} alt={photo.alt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200" />
                        </div>
                        <div className="absolute top-1.5 right-1.5">
                          <input type="checkbox" checked={selectedPhotos.has(photo.id)} onChange={() => {
                            setSelectedPhotos(prev => {
                              const next = new Set(prev);
                              next.has(photo.id) ? next.delete(photo.id) : next.add(photo.id);
                              return next;
                            });
                          }} className="w-4 h-4 rounded accent-blue-500 cursor-pointer" onClick={e => e.stopPropagation()} />
                        </div>
                        {photo.gpsLat != null && (
                          <div className="absolute top-1.5 left-1.5 bg-black/50 text-white rounded-full p-1" title={photo.gpsAddress || 'Geotagged'}>
                            <Icon name="MapPinIcon" size={11} variant="solid" />
                          </div>
                        )}
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-1.5">
                          <p className="text-xs text-white truncate font-medium">{photo.uploadedBy}</p>
                          {photo.gpsAddress ? (
                            <p className="text-[10px] text-white/80 truncate">{photo.gpsAddress}</p>
                          ) : (
                            <p className="text-[10px] text-white/70 truncate">{photo.uploadedAt}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {selectedPhotos.size > 0 && (
                  <button onClick={handleArchivePhotos} className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium text-white transition-opacity hover:opacity-90 bg-red-600">
                    <Icon name="TrashIcon" size={16} variant="solid" />
                    Remove {selectedPhotos.size} Photo{selectedPhotos.size > 1 ? 's' : ''}
                  </button>
                )}
              </div>
            )}

            {/* INSPECTION TAB */}
            {activeTab === 'inspection' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <h3 className="font-heading text-base font-semibold flex items-center gap-2" style={{ color: '#1C2E5E' }}>
                    <Icon name="ClipboardDocumentCheckIcon" size={18} variant="solid" className="text-blue-500" />
                    Inspection Reports
                  </h3>
                  <button
                    onClick={handleNewInspection}
                    disabled={creatingInspection}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold text-white shadow-sm disabled:opacity-60"
                    style={{ backgroundColor: '#E89A3C' }}
                  >
                    {creatingInspection ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Icon name="PlusIcon" size={15} variant="solid" />}
                    New Inspection
                  </button>
                </div>

                {inspectionsLoading ? (
                  <div className="flex items-center justify-center py-10">
                    <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : inspections.length === 0 ? (
                  <button
                    onClick={handleNewInspection}
                    className="w-full flex flex-col items-center justify-center py-12 text-center border-2 border-dashed border-gray-200 rounded-xl hover:border-blue-300 hover:bg-blue-50/40 transition-colors"
                  >
                    <Icon name="ClipboardDocumentCheckIcon" size={36} className="text-gray-300 mb-2" />
                    <p className="text-sm font-medium text-gray-500">No inspection reports yet</p>
                    <p className="text-xs text-gray-400 mt-0.5">Build a photo inspection report to send to the customer.</p>
                  </button>
                ) : (
                  <div className="space-y-2">
                    {inspections.map((ins: any) => (
                      <div key={ins.id} className="flex items-center justify-between gap-3 bg-gray-50 rounded-xl p-4 border border-gray-100">
                        <button onClick={() => setOpenInspectionId(ins.id)} className="flex items-center gap-3 min-w-0 text-left flex-1">
                          <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: '#1C2E5E' }}>
                            <Icon name="ClipboardDocumentCheckIcon" size={17} className="text-white" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-gray-800 truncate">{ins.title || 'Roof Inspection'}</p>
                            <p className="text-xs text-gray-400">
                              {ins.inspection_date ? new Date(ins.inspection_date).toLocaleDateString('en-US') : '—'}
                              <span className="mx-1.5">·</span>
                              <span className="capitalize">{ins.status || 'draft'}</span>
                            </p>
                          </div>
                        </button>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <button onClick={() => setOpenInspectionId(ins.id)} className="px-3 py-1.5 rounded-md text-xs font-semibold text-white" style={{ backgroundColor: '#1C2E5E' }}>Open</button>
                          <button onClick={() => archiveInspection(ins.id)} className="p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50" aria-label="Remove inspection">
                            <Icon name="TrashIcon" size={14} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* MILESTONES TAB */}
            {activeTab === 'milestones' && (
              <div className="space-y-4">
                <h3 className="font-heading text-base font-semibold flex items-center gap-2" style={{ color: '#1C2E5E' }}>
                  <Icon name="FlagIcon" size={18} variant="solid" className="text-blue-500" />
                  Milestones
                </h3>
                {dataLoading ? (
                  <div className="flex items-center justify-center py-10">
                    <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : milestones.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <Icon name="FlagIcon" size={36} className="text-gray-300 mb-3" />
                    <p className="text-sm text-gray-400">No milestones recorded yet.</p>
                  </div>
                ) : (
                  <div className="relative">
                    {milestones.map((m: any, idx: number) => {
                      const cfg = milestoneConfig[m.status as keyof typeof milestoneConfig];
                      return (
                        <div key={m.id} className="flex gap-3 relative">
                          <div className="flex flex-col items-center">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${cfg.bg}`}>
                              <Icon name={cfg.icon as any} size={16} variant={m.status === 'completed' ? 'solid' : 'outline'} className={cfg.color} />
                            </div>
                            {idx < milestones.length - 1 && (
                              <div className={`w-0.5 flex-1 min-h-[20px] my-1 ${cfg.line}`} />
                            )}
                          </div>
                          <div className="pb-4 flex-1">
                            <p className={`text-sm font-medium ${m.status === 'upcoming' ? 'text-gray-400' : 'text-gray-800'}`}>{m.label}</p>
                            <p className="text-xs text-gray-400">{m.date}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* NOTES TAB */}
            {activeTab === 'notes' && (
              <div className="space-y-4">
                <h3 className="font-heading text-base font-semibold flex items-center gap-2" style={{ color: '#1C2E5E' }}>
                  <Icon name="PencilSquareIcon" size={18} variant="solid" className="text-blue-500" />
                  Account Notes
                </h3>
                <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                  <textarea
                    rows={3}
                    value={newNoteText}
                    onChange={e => setNewNoteText(e.target.value)}
                    placeholder="Enter a note about this account..."
                    className="w-full px-3 py-2.5 rounded-lg border border-gray-200 font-body text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 resize-none"
                  />
                  <div className="flex justify-end">
                    <button
                      onClick={handleSaveNote}
                      disabled={savingNote || !newNoteText.trim()}
                      className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white disabled:opacity-50 transition-opacity hover:opacity-90"
                      style={{ backgroundColor: '#1C2E5E' }}
                    >
                      {savingNote ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Icon name="PlusIcon" size={15} variant="solid" />}
                      Save Note
                    </button>
                  </div>
                </div>
                {dataLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : notes.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-10 text-center">
                    <Icon name="PencilSquareIcon" size={36} className="text-gray-300 mb-3" />
                    <p className="text-sm text-gray-400">No notes yet. Add the first note above.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {notes.map((note: any) => (
                      <div key={note.id} className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
                        <div className="flex items-start gap-3">
                          <p className="text-sm text-gray-700 flex-1 whitespace-pre-wrap">{note.content}</p>
                          <button onClick={() => handleDeleteNote(note.id)} className="flex-shrink-0 p-1 rounded-md hover:bg-red-50 text-gray-300 hover:text-red-400 transition-colors" aria-label="Delete note">
                            <Icon name="TrashIcon" size={14} />
                          </button>
                        </div>
                        <div className="flex items-center gap-2 mt-2.5 pt-2.5 border-t border-gray-50">
                          <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                            <Icon name="UserIcon" size={11} className="text-blue-500" />
                          </div>
                          <span className="text-xs text-gray-400">{note.created_by || 'Staff'}</span>
                          <span className="text-gray-200">·</span>
                          <span className="text-xs text-gray-400">
                            {note.created_at ? new Date(note.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* DOCUMENTS TAB */}
            {activeTab === 'documents' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <h3 className="font-heading text-base font-semibold flex items-center gap-2" style={{ color: '#1C2E5E' }}>
                    <Icon name="DocumentTextIcon" size={18} variant="solid" className="text-blue-500" />
                    Documents
                  </h3>
                  <label className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white cursor-pointer transition-opacity hover:opacity-90" style={{ backgroundColor: '#1C2E5E' }}>
                    {uploadingDoc ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Icon name="ArrowUpTrayIcon" size={15} variant="solid" />}
                    Upload Document
                    <input type="file" className="hidden" onChange={handleDocUpload} accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg" />
                  </label>
                </div>
                {docUploadSuccess && (
                  <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2.5">
                    <Icon name="CheckCircleIcon" size={16} variant="solid" className="text-green-600" />
                    <span className="text-sm text-green-700">Document uploaded successfully!</span>
                  </div>
                )}
                {dataLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : documents.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <Icon name="DocumentTextIcon" size={36} className="text-gray-300 mb-3" />
                    <p className="text-sm text-gray-400">No documents uploaded yet.</p>
                    <p className="text-xs text-gray-300 mt-1">Upload contracts, permits, or other files above.</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {documents.map((doc: any) => {
                      const ext = doc.file_name?.split('.').pop()?.toLowerCase() ?? '';
                      const isPdf = ext === 'pdf';
                      const isImage = ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext);
                      const iconName = isPdf ? 'DocumentTextIcon' : isImage ? 'PhotoIcon' : 'DocumentIcon';
                      const iconColor = isPdf ? 'text-red-500' : isImage ? 'text-blue-500' : 'text-gray-500';
                      return (
                        <div key={doc.id} className="flex items-center gap-3 bg-white border border-gray-100 rounded-xl px-4 py-3 shadow-sm">
                          <div className="w-9 h-9 rounded-lg bg-gray-50 flex items-center justify-center flex-shrink-0">
                            <Icon name={iconName as any} size={18} className={iconColor} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-800 truncate">{doc.file_name}</p>
                            <p className="text-xs text-gray-400">
                              {doc.file_size_kb > 0 ? `${doc.file_size_kb} KB · ` : ''}
                              {doc.uploaded_by || 'Staff'} · {doc.created_at ? new Date(doc.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                            </p>
                          </div>
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            <a href={doc.file_url} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded-md hover:bg-blue-50 text-gray-400 hover:text-blue-500 transition-colors" aria-label="View document">
                              <Icon name="EyeIcon" size={15} />
                            </a>
                            <button onClick={() => handleDeleteDoc(doc.id)} className="p-1.5 rounded-md hover:bg-red-50 text-gray-300 hover:text-red-400 transition-colors" aria-label="Delete document">
                              <Icon name="TrashIcon" size={15} />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* MEASUREMENTS TAB */}
            {activeTab === 'measurements' && (
              <div className="space-y-5">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <h3 className="font-heading text-base font-semibold flex items-center gap-2" style={{ color: '#1C2E5E' }}>
                    <Icon name="CalculatorIcon" size={18} variant="solid" className="text-blue-500" />
                    Roof Measurements
                  </h3>
                  <button
                    onClick={() => setShowMeasureTool(true)}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold text-white shadow-sm"
                    style={{ backgroundColor: '#E89A3C' }}
                  >
                    <Icon name="MapIcon" size={15} variant="solid" />
                    {measurements ? 'Edit Measurement' : 'Measure Roof'}
                  </button>
                </div>
                {dataLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : !measurements ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <Icon name="CalculatorIcon" size={36} className="text-gray-300 mb-3" />
                    <p className="text-sm text-gray-400">No roof measurements recorded yet.</p>
                    <p className="text-xs text-gray-300 mt-1">Measurements will appear here once entered.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {[
                        { label: 'Total Squares', value: measurements.total_squares ? `${measurements.total_squares} sq` : '—', icon: 'HomeIcon', color: 'bg-blue-50 text-blue-600' },
                        { label: 'Pitch', value: measurements.pitch || '—', icon: 'ArrowTrendingUpIcon', color: 'bg-purple-50 text-purple-600' },
                        { label: 'Stories', value: measurements.stories ? `${measurements.stories}` : '—', icon: 'BuildingOfficeIcon', color: 'bg-indigo-50 text-indigo-600' },
                        { label: 'Waste Factor', value: measurements.waste_factor_pct ? `${measurements.waste_factor_pct}%` : '—', icon: 'ExclamationTriangleIcon', color: 'bg-yellow-50 text-yellow-600' },
                      ].map(item => (
                        <div key={item.label} className={`rounded-xl p-3.5 ${item.color.split(' ')[0]}`}>
                          <div className={`flex items-center gap-1.5 mb-1 ${item.color.split(' ')[1]}`}>
                            <Icon name={item.icon as any} size={14} />
                            <span className="text-xs font-medium">{item.label}</span>
                          </div>
                          <p className={`text-lg font-bold ${item.color.split(' ')[1]}`}>{item.value}</p>
                        </div>
                      ))}
                    </div>
                    <div className="bg-gray-50 rounded-xl p-4">
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Linear Footage</p>
                      <div className="space-y-2">
                        {[
                          { label: 'Ridges', value: measurements.ridges_lf, unit: 'LF' },
                          { label: 'Hips', value: measurements.hips_lf, unit: 'LF' },
                          { label: 'Valleys', value: measurements.valleys_lf, unit: 'LF' },
                          { label: 'Rakes', value: measurements.rakes_lf, unit: 'LF' },
                          { label: 'Eaves', value: measurements.eaves_lf, unit: 'LF' },
                          { label: 'Transition', value: measurements.transition_lf, unit: 'LF' },
                          { label: 'Step Flashing', value: measurements.step_flashing_lf, unit: 'LF' },
                          { label: 'Headwall Flashing', value: measurements.headwall_flashing_lf, unit: 'LF' },
                        ].map(row => (
                          <div key={row.label} className="flex items-center justify-between py-1.5 border-b border-gray-100 last:border-0">
                            <span className="text-sm text-gray-600">{row.label}</span>
                            <span className="text-sm font-semibold text-gray-800">{row.value ? `${row.value} ${row.unit}` : '—'}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-400">
                      <Icon name="UserIcon" size={13} />
                      <span>Measured by {measurements.measured_by || 'System'}</span>
                      {measurements.measured_at && (
                        <>
                          <span>·</span>
                          <span>{new Date(measurements.measured_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                        </>
                      )}
                    </div>
                    {measurements.notes && (
                      <div className="bg-yellow-50 border border-yellow-100 rounded-lg px-3 py-2.5">
                        <p className="text-xs text-yellow-700">{measurements.notes}</p>
                      </div>
                    )}
                  </div>
                )}
                {!dataLoading && !measurements && card.totalSquares > 0 && (
                  <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
                    <p className="text-xs font-semibold text-blue-500 uppercase tracking-wider mb-2">From Estimate</p>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Total Squares</span>
                      <span className="text-lg font-bold text-blue-700">{card.totalSquares} sq</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ESTIMATE TAB */}
            {activeTab === 'estimate' && (
              <div className="space-y-5">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <h3 className="font-heading text-base font-semibold flex items-center gap-2" style={{ color: '#1C2E5E' }}>
                    <Icon name="CurrencyDollarIcon" size={18} variant="solid" className="text-blue-500" />
                    Estimates
                  </h3>
                  <button
                    onClick={handleNewEstimate}
                    disabled={creatingEstimate}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50 hover:opacity-90"
                    style={{ backgroundColor: '#1C2E5E' }}
                  >
                    {creatingEstimate
                      ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      : <Icon name="PlusIcon" size={15} variant="solid" />}
                    New Estimate
                  </button>
                </div>

                {estimateError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2.5 text-sm text-red-600">
                    Couldn&rsquo;t create estimate: {estimateError}. If this mentions a missing table or column, run the estimates migration SQL.
                  </div>
                )}

                {loadingEstimates ? (
                  <div className="py-10 flex justify-center"><div className="w-6 h-6 border-2 border-gray-300 border-t-transparent rounded-full animate-spin" /></div>
                ) : estimatesList.length === 0 ? (
                  <div className="text-center py-12 bg-gray-50 rounded-xl">
                    <Icon name="DocumentTextIcon" size={28} className="text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500 font-medium">No estimates yet</p>
                    <p className="text-xs text-gray-400 mt-1">Click &ldquo;New Estimate&rdquo; to build one.</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {estimatesList.map(est => (
                      <button
                        key={est.id}
                        onClick={() => router.push(`/estimates/${est.id}`)}
                        className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl border border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50/30 transition-colors text-left"
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-800 truncate">{est.estimate_name || est.estimate_number || 'Estimate'}</p>
                          <p className="text-xs text-gray-400">{est.created_at ? new Date(est.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}</p>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          <span className="text-sm font-bold" style={{ color: '#1C2E5E' }}>{est.total > 0 ? formatCurrency(est.total) : '—'}</span>
                          <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${est.status === 'signed' ? 'bg-green-100 text-green-700' : est.status === 'sent' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>{est.status}</span>
                          <Icon name="ChevronRightIcon" size={16} className="text-gray-300" />
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'permits' && (
              <div className="space-y-5">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <h3 className="font-heading text-base font-semibold flex items-center gap-2" style={{ color: '#1C2E5E' }}>
                    <Icon name="DocumentCheckIcon" size={18} variant="solid" className="text-blue-500" />
                    Permits
                  </h3>
                  <span className="text-xs text-gray-400">Job #{card.jobId || '—'}</span>
                </div>

                {permitLoading ? (
                  <div className="py-10 flex justify-center"><div className="w-6 h-6 border-2 border-gray-300 border-t-transparent rounded-full animate-spin" /></div>
                ) : permit ? (
                  <div className="border border-gray-200 rounded-xl p-4 space-y-3">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div>
                        <p className="text-sm font-semibold" style={{ color: '#1C2E5E' }}>{permit.jurisdiction_name || 'Jurisdiction not identified'}</p>
                        <p className="text-xs text-gray-500">{permit.permit_number ? `Permit #${permit.permit_number}` : 'No permit number yet'}</p>
                      </div>
                      <span className="text-[10px] font-bold uppercase px-2.5 py-1 rounded-full bg-blue-50 text-blue-700">
                        {permit.status.replace(/_/g, ' ')}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="bg-gray-50 rounded-lg px-3 py-2">
                        <p className="text-gray-400">Fee</p>
                        <p className="font-semibold text-gray-700">{permit.fee_amount != null ? `$${Number(permit.fee_amount).toLocaleString()}` : '—'}{permit.fee_paid ? ' · Paid' : ''}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg px-3 py-2">
                        <p className="text-gray-400">Inspections</p>
                        <p className="font-semibold text-gray-700">{Array.isArray(permit.inspections) ? permit.inspections.length : 0}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowPermitDetail(true)}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white hover:opacity-90"
                      style={{ backgroundColor: '#1C2E5E' }}
                    >
                      <Icon name="DocumentCheckIcon" size={15} variant="solid" />
                      Open Permit Process &amp; Features
                    </button>
                  </div>
                ) : (
                  <div className="text-center py-12 bg-gray-50 rounded-xl">
                    <Icon name="DocumentCheckIcon" size={28} className="text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500 font-medium">No permit on file for this job</p>
                    <p className="text-xs text-gray-400 mt-1 mb-4">Start the permitting process for Job #{card.jobId || '—'}.</p>
                    <button
                      onClick={handleCreatePermit}
                      disabled={permitCreating || !card.jobId}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50 hover:opacity-90"
                      style={{ backgroundColor: '#1C2E5E' }}
                    >
                      {permitCreating
                        ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        : <Icon name="PlusIcon" size={15} variant="solid" />}
                      Create Permit
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Permit Detail — per-customer permitting process + features */}
      {showPermitDetail && permit && (
        <PermitDetailModal
          permit={permit}
          jobAddress={[card.address, card.city, card.state].filter(Boolean).join(', ')}
          onClose={() => { setShowPermitDetail(false); setPermitFetched(false); }}
        />
      )}

      {/* Lightbox */}
      {lightboxPhoto && (
        <div className="fixed inset-0 bg-black/80 z-[170] flex items-center justify-center p-4" onClick={() => setLightboxPhoto(null)}>
          <div className="relative max-w-2xl w-full" onClick={e => e.stopPropagation()}>
            <button onClick={() => setLightboxPhoto(null)} className="absolute -top-10 right-0 text-white hover:text-gray-300 transition-colors" aria-label="Close lightbox">
              <Icon name="XMarkIcon" size={28} />
            </button>
            <div className="rounded-xl overflow-hidden">
              <AppImage src={lightboxPhoto.src} alt={lightboxPhoto.alt} className="w-full object-contain max-h-[70vh]" />
            </div>
            {(lightboxPhoto.uploadedBy || lightboxPhoto.uploadedAt) && (
              <div className="mt-2 flex items-center justify-between text-xs text-white/80">
                <span>{lightboxPhoto.uploadedBy}</span>
                <span>{lightboxPhoto.uploadedAt}</span>
              </div>
            )}
            {lightboxPhoto.gpsLat != null && (
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${lightboxPhoto.gpsLat},${lightboxPhoto.gpsLng}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-1.5 flex items-start gap-1.5 text-xs text-white/80 hover:text-white transition-colors"
                onClick={e => e.stopPropagation()}
              >
                <Icon name="MapPinIcon" size={13} variant="solid" className="text-[#E89A3C] mt-0.5 flex-shrink-0" />
                <span>
                  {lightboxPhoto.gpsAddress || `${Number(lightboxPhoto.gpsLat).toFixed(6)}, ${Number(lightboxPhoto.gpsLng).toFixed(6)}`}
                  <span className="text-white/50"> · Open in Maps</span>
                </span>
              </a>
            )}
          </div>
        </div>
      )}

      {/* Change Order Modal */}
      {showChangeOrder && (
        <ChangeOrderModal
          jobId={card.jobId}
          customerName={fullName}
          onClose={() => setShowChangeOrder(false)}
        />
      )}

      {/* Edit Customer (reuses the New Customer form). Panel refreshes via the
          board's realtime subscription after save. */}
      {showEditCustomer && (
        <AddCustomerModal
          editCard={card}
          onClose={() => setShowEditCustomer(false)}
        />
      )}

      {/* Inspection report builder (full-screen) */}
      {openInspectionId && (
        <InspectionBuilder
          inspectionId={openInspectionId}
          pipelineId={card.id}
          customer={{
            name: fullName,
            address: card.address,
            city: card.city,
            state: card.state,
            zip: card.zip,
            phone: card.phone,
            email: card.email,
          }}
          onClose={() => { setOpenInspectionId(null); loadInspections(); }}
        />
      )}

      {/* Roof measurement tool (full-screen) */}
      {showMeasureTool && (
        <RoofMeasureTool
          pipelineId={card.id}
          address={[card.address, card.city, card.state, card.zip].filter(Boolean).join(', ')}
          customerName={fullName}
          initialSketch={measurements?.sketch ?? null}
          onClose={() => setShowMeasureTool(false)}
        />
      )}

      {/* Escalation Modal */}
      {escalateModalOpen && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center gap-3 px-6 pt-6 pb-4 border-b border-gray-100">
              <div className="w-9 h-9 rounded-xl bg-red-100 flex items-center justify-center flex-shrink-0">
                <Icon name="ExclamationTriangleIcon" size={18} className="text-red-600" />
              </div>
              <div>
                <h3 className="text-[15px] font-bold text-gray-900">Describe the Problem</h3>
                <p className="text-[12px] text-gray-500">Job #{card.jobId} · {fullName}</p>
              </div>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-[13px] font-semibold text-gray-700 mb-2">
                  Problem Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-[13px] text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-transparent resize-none"
                  rows={4}
                  placeholder="Describe the issue in detail so the team understands the problem…"
                  value={escalateDesc}
                  onChange={e => setEscalateDesc(e.target.value)}
                  autoFocus
                />
                <p className="text-[11px] text-gray-400 mt-1.5">This will be included at the top of the escalation ticket.</p>
              </div>
              <div>
                <label className="block text-[13px] font-semibold text-gray-700 mb-2">
                  Escalate To <span className="text-red-500">*</span>
                  <span className="ml-1 text-[11px] font-normal text-gray-400">(select one or more departments)</span>
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {['Logistics', 'Quality Control', 'Billing', 'Finance', 'Operations', 'Executive'].map(dept => {
                    const selected = escalateDepts.includes(dept);
                    return (
                      <button
                        key={dept}
                        type="button"
                        onClick={() => setEscalateDepts(prev =>
                          selected ? prev.filter(d => d !== dept) : [...prev, dept]
                        )}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-[12px] font-semibold transition-all text-left ${
                          selected
                            ? 'border-red-500 bg-red-50 text-red-700' :'border-gray-200 bg-gray-50 text-gray-600 hover:border-gray-300 hover:bg-gray-100'
                        }`}
                      >
                        <div className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 border ${
                          selected ? 'bg-red-500 border-red-500' : 'border-gray-300 bg-white'
                        }`}>
                          {selected && (
                            <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                        {dept}
                      </button>
                    );
                  })}
                </div>
                {escalateDepts.length > 0 && (
                  <p className="text-[11px] text-red-600 mt-2 font-medium">
                    Will create {escalateDepts.length} ticket{escalateDepts.length > 1 ? 's' : ''}: {escalateDepts.join(', ')}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 pb-6">
              <button
                type="button"
                onClick={() => { setEscalateModalOpen(false); setEscalateDepts([]); }}
                className="px-4 py-2 rounded-lg text-[13px] font-semibold text-gray-600 hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!escalateDesc.trim() || escalateDepts.length === 0 || escalateLoading}
                onClick={handleEscalate}
                className="px-5 py-2 rounded-lg text-[13px] font-semibold text-white bg-red-600 hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {escalateLoading && (
                  <svg className="w-3.5 h-3.5 animate-spin" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3"/>
                    <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                  </svg>
                )}
                {escalateLoading ? 'Submitting…' : `Submit Escalation${escalateDepts.length > 1 ? ` (${escalateDepts.length})` : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
